import { Role, DbRef, DraftWaypointStatus, WaypointStatusName, WaypointActionType, ShiftStatus, WaypointActionStatusName, SamplesDeliveryType, InstructionType, WaypointType, FeatureFlag, ItineraryStatus, DraftWaypointSupplyStrategy, ProcedureStatus, OfficeType } from '@caresend/types';
import { isEncrypted, objectFilter, objectMap, nullishFilter, getShipmentPath, getDraftWaypointPath, getItineraryPath, formatFullName, hasStatus, arrayToObj, generateID, timeToMilliseconds, initWaypoint, objectSome, formatCamelCase, getOrderPath, initTimeZoneDate, objectReduce, getProcedurePath, formatAddressUS, formatPhoneNumber, buildBulletSeparatedString, formatDateOfBirth, isNullish, getPlacePath, getPlaceGroupPath, getShiftPath, getCityByLocation, getPostalCodeByLocation, getNeighborhoodByLocation, changeTimeZone, getUserPath, getLatLngDeviationInMiles, getWaypointActionPath, getWaypointPath, timeInMsToHoursAndMinutes, AddressFormatType, getStateByLocation, getLatLngDeviationInFt, getProcedureSamplePath } from '@caresend/utils';
import dayjs from 'dayjs';
import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import { defineStore } from 'pinia';
import { reactive, computed, watch } from 'vue';
import { getChartingItemByIndexArray } from './helpers/charting.js';
import { getWaypointStatusFromInfoTitle } from './helpers/draftWaypoint.js';
import { extractTarget, extractTargetOrPriority, generateWaypointActions } from './helpers/helpers.js';
import { getDropOffScanSpecimenBag, getInitialProcedureKitSupply, getMailInBoxSupply, getPackingScanSpecimenBags } from './scannableItems.js';
import { formatDate, formatTime, displayMinutes } from './utils/date.js';
import { injectNulls } from './utils/injectNulls.js';
import { DropOffWorkflowItemID, PackingWorkflowItemID, SampleWorkflowContextID, WaypointActionWorkflowItemID } from './workflow/items/model.js';
import { getProcedureWorkflow } from './workflow/items/procedures.js';
import { getSampleWorkflow } from './workflow/items/sample.js';
import { getWaypointWorkflow } from './workflow/items/waypoint.js';
import { getWaypointActionWorkflow } from './workflow/items/waypointActions.js';
import { workflowUtils } from './workflow/utils.js';
import { useDbSyncStore } from '../storeUtilities/dbSync/dbSyncStore.js';
import { ls } from '../storeUtilities/persist/persist.js';
import { recordManager } from '../storeUtilities/recordManager/recordManager.js';
import { autoState } from '../storeUtilities/state/state.js';
import { getPolygonOrMultiPolygonCentroid, getAveragePoint } from '../../../../ui/Map/helpers/private/index.js';
import { watchOnce } from '../../../../../composables/dom.js';
import { getStore } from '../../../../../store/helpers/initStore.js';
import '../../../../../router/helpers/initRouter.js';
import 'file-saver';
import '../../../../../database/firebaseConfig/cloudFunctions.js';
import { getEncryptionCachedData } from '../../../../../database/encryption/caching.js';
import '../../../../../database/encryption/model.js';
import { generateHash } from '../../../../../database/encryption/utils/helpers.js';
import { getExtendedSample } from '../../../../../functions/samples.js';
import { initItinerary } from '../../../../../store/modules/itineraries/helpers.js';
import { getProcedureDateTimeInfo } from '../../../../../store/modules/procedures/helpers.js';

/* eslint-disable no-underscore-dangle */
/* eslint sort-keys-fix/sort-keys-fix: "error" */
const initialItinerary = injectNulls(initItinerary());
const dateParams = {
    noTimezone: true,
    yearOptional: true,
};
const usePortalStore = defineStore('itinerary', () => {
    var _a, _b, _c;
    const store = getStore();
    const itinerary = reactive(initialItinerary);
    /**
     * Reserved for shallow minimal state values
     */
    const state = autoState({
        colorScheme: 'light',
        currentLocationStart: false,
        debugMode: ((_a = store.state.auth.user) === null || _a === void 0 ? void 0 : _a.role) === Role.SUPERADMIN
            && !!store.state.variables.featureFlags.debug,
        isItineraryFound: false,
        isReady: false,
        patientNamesRedacted: false,
    });
    /**
     * State values for assigning a user to a shift
     * It's important for all relationships to be collected prior to allowing
     * the search to have the proper parameters to filter.
     */
    const shiftAssignmentState = autoState({
        isReady: false,
        shift: null,
    });
    /**
     * The role that the user is viewing the itinerary as. This is allowed
     * to be emulated by SuperAdmins to test or troubleshoot the application
     * as other roles would interact with the application.
     * Persistent across sessions to maintain consistency in testing.
     */
    const viewAs = ls.ref('itinerary/viewAsRole', (_c = (_b = store.state.auth.user) === null || _b === void 0 ? void 0 : _b.role) !== null && _c !== void 0 ? _c : Role.NURSE);
    watchOnce(viewAs, () => {
        var _a, _b, _c;
        if (((_a = store.state.auth.user) === null || _a === void 0 ? void 0 : _a.role) !== Role.SUPERADMIN
            && viewAs.value === Role.SUPERADMIN) {
            viewAs.value = (_c = (_b = store.state.auth.user) === null || _b === void 0 ? void 0 : _b.role) !== null && _c !== void 0 ? _c : Role.NURSE;
        }
    });
    const setViewAs = (role) => {
        viewAs.value = role;
    };
    const viewAsRoleGetters = Object.fromEntries(Object.values(Role).map((role) => [role, computed(() => viewAs.value === role)]));
    const patientNamesRedactedPersisted = ls.ref('itinerary/patientNamesRedacted', state.value.patientNamesRedacted);
    watchOnce(patientNamesRedactedPersisted, () => {
        state.set.patientNamesRedacted(patientNamesRedactedPersisted.value);
    });
    /**
     * All records managed by the itinerary store
     * Maintained as an object to support self referencing computation functions
     * `_` appended to ensure the housing for the managers are retained privately
     *
     * @note It is imperative that any computed functions defined referencing another recordManager
     * or self-referencing recordManager are called with `getRaw` to ensure the computation functions
     * don’t result in infinite update loops.
     */
    const _r = {
        bookings: recordManager()({
            patient: (booking) => {
                var _a;
                const b = extractTarget(booking, _r.bookings.getRaw);
                if (!b)
                    return;
                let { patientID } = b;
                if (isEncrypted(patientID)) {
                    patientID = getEncryptionCachedData(patientID.id) || patientID;
                }
                return (_a = _r.users.getRaw(patientID)) !== null && _a !== void 0 ? _a : store.state.users.users[patientID];
            },
            procedures: (booking) => {
                var _a;
                const b = extractTarget(booking, _r.bookings.getRaw);
                if (!b)
                    return {};
                return objectFilter(objectMap((_a = b.procedures) !== null && _a !== void 0 ? _a : {}, (_, id) => _r.procedures.getRaw(id)), nullishFilter);
            },
        }),
        denormalizedOrders: recordManager()({}),
        draftShipments: recordManager({
            context: DbRef.ITINERARIES,
            label: 'shipment',
            pathGetter: getShipmentPath,
        })({}),
        draftWaypoints: recordManager({
            context: DbRef.ITINERARIES,
            label: 'draft waypoint',
            pathGetter: getDraftWaypointPath,
        })({
            existingPreview: (draftWaypoint) => {
                const dw = extractTarget(draftWaypoint, _r.draftWaypoints.getRaw);
                if (!(dw === null || dw === void 0 ? void 0 : dw.waypointID))
                    return;
                const previewWaypoints = _r.waypoints.filterRaw((waypoint) => { var _a; return !!((_a = waypoint === null || waypoint === void 0 ? void 0 : waypoint.draftWaypoints) === null || _a === void 0 ? void 0 : _a[dw.id]); });
                const waypoint = Object.values(previewWaypoints !== null && previewWaypoints !== void 0 ? previewWaypoints : {})[0];
                if (!waypoint)
                    return;
                const isWaypointCombinableByID = _r.itinerary.getters.isWaypointCombinableByID(waypoint);
                const waypointActions = _r.waypoints.getters.waypointActions(waypoint.id);
                return {
                    draftWaypointID: dw.id,
                    isWaypointCombinableByID,
                    waypoint,
                    waypointActions,
                };
            },
            fulfillmentStatus: (draftWaypoint) => {
                const dw = extractTarget(draftWaypoint, _r.draftWaypoints.getRaw);
                if (!dw)
                    return DraftWaypointStatus.PENDING;
                return dw.status;
            },
            waypoint: (draftWaypoint) => {
                const dw = extractTarget(draftWaypoint, _r.draftWaypoints.getRaw);
                if (dw === null || dw === void 0 ? void 0 : dw.waypointID) {
                    const wp = _r.waypoints.getRaw(dw.waypointID);
                    if (wp)
                        return wp;
                }
                if (dw === null || dw === void 0 ? void 0 : dw.itineraryID) {
                    const itin = _r.itineraries.get(dw.itineraryID);
                    const wp = itin === null || itin === void 0 ? void 0 : itin.computed.waypoints.find((w) => { var _a; return (_a = w.draftWaypoints) === null || _a === void 0 ? void 0 : _a[dw.id]; });
                    if (wp)
                        return wp;
                }
                return null;
            },
        }, {
            preview: (draftWaypoint) => {
                var _a;
                const dw = extractTarget(draftWaypoint, _r.draftWaypoints.getRaw);
                const getDefault = () => {
                    var _a, _b;
                    return ({
                        draftWaypointID: (_a = dw === null || dw === void 0 ? void 0 : dw.id) !== null && _a !== void 0 ? _a : '',
                        ..._r.itinerary.getters.initWaypointWithSuggestions({
                            draftWaypointID: (_b = dw === null || dw === void 0 ? void 0 : dw.id) !== null && _b !== void 0 ? _b : '',
                            waypointType: WaypointType.PATIENT,
                        }),
                    });
                };
                if (!dw)
                    return getDefault();
                return (_a = _r.draftWaypoints.getters.existingPreview(draftWaypoint)) !== null && _a !== void 0 ? _a : getDefault();
            },
        }),
        itineraries: recordManager({
            context: DbRef.ITINERARIES,
            label: 'itinerary',
            pathGetter: getItineraryPath,
        })({
            assignedNurse: (itineraryID) => {
                var _a;
                const i = extractTarget(itineraryID, _r.itineraries.getRaw);
                if (!(i === null || i === void 0 ? void 0 : i.nurseID))
                    return;
                return (_a = _r.users.getRaw(i.nurseID)) !== null && _a !== void 0 ? _a : store.state.users.users[i.nurseID];
            },
            draftWaypointIDs: (itineraryID) => _r.itineraries.getters.waypoints(itineraryID).reduce((acc, waypoint) => ({
                ...acc,
                ...waypoint.draftWaypoints,
            }), {}),
            draftWaypoints: (itineraryID) => _r.itineraries.getters.waypoints(itineraryID).reduce((acc, waypoint) => ({
                ...acc,
                ..._r.waypoints.getters.draftWaypoints(waypoint.id),
            }), {}),
            itinerarySession: (itineraryID) => {
                var _a, _b;
                const i = extractTarget(itineraryID, _r.itineraries.getRaw);
                if (!i)
                    return;
                const nurse = _r.itineraries.getters.assignedNurse(i);
                const name = formatFullName(nurse, false, false);
                const title = nurse
                    ? `${name}`
                    : 'Unassigned';
                const serviceRegions = _r.itineraries.getters.serviceRegionsFromCache(i);
                const serviceRegionNames = Object.values(serviceRegions).map((sr) => sr.name).join(', ');
                return {
                    date: i.dateInfo.date,
                    id: i.id,
                    img: nurse === null || nurse === void 0 ? void 0 : nurse.info.picture,
                    sessionID: i.id,
                    statusDbRef: getItineraryPath(i.id, 'status'),
                    subtitle: `${serviceRegionNames}`,
                    time: (_a = i.startTime) !== null && _a !== void 0 ? _a : undefined,
                    title,
                    userID: (_b = i.nurseID) !== null && _b !== void 0 ? _b : undefined,
                };
            },
            nextActionableWaypoint: (itineraryID) => {
                const waypoints = _r.itineraries.getters.waypoints(itineraryID);
                const nextWaypoint = waypoints.find((wp) => !hasStatus(wp, [
                    WaypointStatusName.COMPLETE,
                    WaypointStatusName.DRAFT,
                ]));
                return nextWaypoint;
            },
            procedures: (itineraryID) => Object.values(_r.itineraries.getters.waypointActions(itineraryID))
                .filter((wpa) => wpa.type === WaypointActionType.PATIENT_ACTION)
                .reduce((currentProcedures, wpa) => ({
                ...currentProcedures,
                ..._r.waypointActions.getters.procedures(wpa.id),
            }), {}),
            serviceRegionsFromCache: (itineraryID) => {
                var _a;
                const i = extractTarget(itineraryID, _r.itineraries.getRaw);
                if (!(i === null || i === void 0 ? void 0 : i.waypoints))
                    return {};
                return {
                    ...(_a = _r.serviceRegionCache.getRaw(i.id)) === null || _a === void 0 ? void 0 : _a.serviceRegions,
                    ..._r.itineraries.getters.waypoints(itineraryID).reduce((acc, waypoint) => {
                        const serviceRegion = _r.waypoints.getters.serviceRegionsFromCache(waypoint);
                        if (!serviceRegion)
                            return acc;
                        return {
                            ...acc,
                            ...serviceRegion,
                        };
                    }, {}),
                };
            },
            shift: (itineraryID) => {
                var _a;
                const i = extractTarget(itineraryID, _r.itineraries.getRaw);
                if (!i)
                    return null;
                return _r.shifts.getRaw((_a = i.shiftID) !== null && _a !== void 0 ? _a : '');
            },
            shiftIsCheckedIn: (itineraryID) => {
                const shift = _r.itineraries.getters.shift(itineraryID);
                return hasStatus(shift, ShiftStatus.IN_PROGRESS);
            },
            stateAndPostalCodes: (itineraryID) => {
                const i = extractTarget(itineraryID, _r.itineraries.getRaw);
                if (!(i === null || i === void 0 ? void 0 : i.waypoints))
                    return { postalCodes: [], states: [] };
                const statesAndPostalCodes = Object.values(i.waypoints)
                    .map((wp) => {
                    const stateAndPostalCode = _r.waypoints.getters.stateAndPostalCode(wp.id);
                    if (!stateAndPostalCode)
                        return;
                    return stateAndPostalCode;
                });
                const states = new Set(statesAndPostalCodes.map((wp) => wp === null || wp === void 0 ? void 0 : wp.state));
                const postalCodes = new Set(statesAndPostalCodes.map((wp) => wp === null || wp === void 0 ? void 0 : wp.postalCode));
                return {
                    postalCodes: Array.from(postalCodes),
                    states: Array.from(states),
                };
            },
            timeZone: (itineraryID) => {
                var _a, _b;
                const i = extractTarget(itineraryID, _r.itineraries.getRaw);
                const waypointID = (_a = _r.itineraries.getters.waypoints(itineraryID)[0]) === null || _a === void 0 ? void 0 : _a.id;
                if (!waypointID)
                    return;
                return (_b = _r.waypoints.getters.timeZone(waypointID)) !== null && _b !== void 0 ? _b : i === null || i === void 0 ? void 0 : i.dateInfo.date.timeZone;
            },
            totalDuration: (itineraryID) => {
                var _a, _b;
                const i = extractTarget(itineraryID, _r.itineraries.getRaw);
                if (!(i === null || i === void 0 ? void 0 : i.routes) && !(i === null || i === void 0 ? void 0 : i.waypoints))
                    return 0;
                return ((_b = (_a = i.routes) === null || _a === void 0 ? void 0 : _a.reduce((total, { duration }) => total + duration, 0)) !== null && _b !== void 0 ? _b : 0)
                    + _r.itineraries.getters.waypoints(itineraryID).reduce((total, waypoint) => total + _r.waypoints.getters.totalDuration(waypoint), 0);
            },
            totalRoutesDuration: (itineraryID) => {
                const i = extractTarget(itineraryID, _r.itineraries.getRaw);
                if (!(i === null || i === void 0 ? void 0 : i.routes))
                    return 0;
                return i.routes.reduce((total, { duration }) => total + duration, 0);
            },
            waypointActions: (itineraryID) => _r.itineraries.getters.waypoints(itineraryID)
                .reduce((acc, waypoint) => ({
                ...acc,
                ...arrayToObj(_r.waypoints.getters.waypointActions(waypoint.id), 'id'),
            }), {}),
            waypoints: (itineraryID) => {
                var _a;
                const i = extractTarget(itineraryID, _r.itineraries.getRaw);
                if (!(i === null || i === void 0 ? void 0 : i.waypoints))
                    return [];
                return (_a = i.waypoints
                    .map(({ id }) => _r.waypoints.getRaw(id))
                    .filter(nullishFilter)) !== null && _a !== void 0 ? _a : [];
            },
        }),
        /**
         * TODO: Implement a getter caching mechanism for a single entity manager
         * similar to recordManager
         */
        itinerary: {
            getters: {
                assignedNurse: (itineraryID) => {
                    const i = extractTargetOrPriority(itineraryID, _r.itineraries.getRaw, itinerary);
                    if (!i)
                        return;
                    return _r.itineraries.getters.assignedNurse(i);
                },
                currentWaypointActions: (itineraryID) => {
                    const i = extractTargetOrPriority(itineraryID, _r.itineraries.getRaw, itinerary);
                    if (!i)
                        return {};
                    return _r.itineraries.getters.waypointActions(i);
                },
                currentWaypoints: (itineraryID) => {
                    const i = extractTargetOrPriority(itineraryID, _r.itineraries.getRaw, itinerary);
                    if (!i)
                        return [];
                    return _r.itineraries.getters.waypoints(i);
                },
                draftWaypointIDs: (itineraryID) => {
                    const i = extractTargetOrPriority(itineraryID, _r.itineraries.getRaw, itinerary);
                    if (!i)
                        return {};
                    return _r.itineraries.getters.draftWaypointIDs(i);
                },
                draftWaypoints: (itineraryID) => {
                    const i = extractTargetOrPriority(itineraryID, _r.itineraries.getRaw, itinerary);
                    if (!i)
                        return {};
                    return _r.itineraries.getters.draftWaypoints(i);
                },
                initWaypointWithSuggestions: (payload) => {
                    var _a, _b, _c, _d, _e, _f, _g, _h;
                    let { location } = payload;
                    const { draftWaypointID, waypointType, place } = payload;
                    const waypointID = generateID();
                    let wpProcedures;
                    let wpas = [];
                    const draftWaypoint = draftWaypointID
                        ? _r.draftWaypoints.getRaw(draftWaypointID)
                        : undefined;
                    if (draftWaypoint && !location && !place) {
                        wpProcedures = draftWaypoint.procedures;
                        location = draftWaypoint.location;
                        wpas = generateWaypointActions(WaypointActionType.PATIENT_ACTION, Object.keys(wpProcedures !== null && wpProcedures !== void 0 ? wpProcedures : {}), waypointID, _r.itinerary.value.id);
                    }
                    let dateTimestamp = (_a = draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.finalizedDate) === null || _a === void 0 ? void 0 : _a.date.timestamp;
                    const dateTimeZone = (_e = (_c = (_b = draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.dateRange) === null || _b === void 0 ? void 0 : _b.startDate.timeZone) !== null && _c !== void 0 ? _c : (_d = draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.finalizedDate) === null || _d === void 0 ? void 0 : _d.date.timeZone) !== null && _e !== void 0 ? _e : dayjs.tz.guess();
                    if (((_f = draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.dateRange) === null || _f === void 0 ? void 0 : _f.startDate.timestamp) && (draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.timeRange)) {
                        const time = ((_h = (_g = Object.values(draftWaypoint.timeRange).sort((a, b) => b.startTime - a.startTime)[0]) === null || _g === void 0 ? void 0 : _g.startTime) !== null && _h !== void 0 ? _h : 0);
                        const timeToAddInMs = timeToMilliseconds(time);
                        dateTimestamp = draftWaypoint.dateRange.startDate.timestamp + timeToAddInMs;
                    }
                    const waypoint = {
                        ...initWaypoint(waypointType, location, place),
                        date: {
                            timeZone: dateTimeZone,
                            timestamp: dateTimestamp,
                        },
                        id: waypointID,
                        itineraryID: itinerary.id,
                        status: {
                            status: WaypointStatusName.DRAFT,
                            timestamp: Date.now(),
                        },
                        waypointActions: wpas.map(({ id }) => ({ id })),
                        ...(draftWaypointID && {
                            draftWaypoints: { [draftWaypointID]: true },
                        }),
                    };
                    const isWaypointCombinableByID = _r.itinerary.getters.currentWaypoints()
                        .filter((wp0) => _r.waypoints.getters.combinableMatch(wp0, waypoint))
                        .map((wp) => wp.id);
                    return {
                        draftWaypointStatus: draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.status,
                        isWaypointCombinableByID,
                        waypoint,
                        waypointActions: wpas,
                    };
                },
                isWaypointCombinableByID: (waypoint, itineraryID) => {
                    const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                    if (!wp)
                        return [];
                    return _r.itineraries.getters.waypoints(itineraryID)
                        .filter((wp0) => _r.waypoints.getters.combinableMatch(wp0, waypoint))
                        .map((wp0) => wp0.id);
                },
                itinerarySession: (itineraryID) => {
                    const i = extractTargetOrPriority(itineraryID, _r.itineraries.getRaw, itinerary);
                    if (!i)
                        return;
                    return _r.itineraries.getters.itinerarySession(i);
                },
                /** Find the next waypoint that is not complete or draft */
                nextActionableWaypoint: (itineraryID) => {
                    const i = extractTargetOrPriority(itineraryID, _r.itineraries.getRaw, itinerary);
                    if (!i)
                        return;
                    const nextWaypoint = _r.itineraries.getters.nextActionableWaypoint(i);
                    return nextWaypoint;
                },
                nextInTransitWaypoint: (itineraryID) => {
                    const nextWaypoint = _r.itinerary.getters.nextActionableWaypoint(itineraryID);
                    if (!nextWaypoint || !hasStatus(nextWaypoint, WaypointStatusName.TRANSIT_WAYPOINT))
                        return;
                    return nextWaypoint;
                },
                numberOfPatients: (itineraryID) => {
                    const i = extractTargetOrPriority(itineraryID, _r.itineraries.getRaw, itinerary);
                    if (!i)
                        return 0;
                    const procedures = _r.itinerary.getters.procedures(itineraryID);
                    const bookingIDs = (Object.values(procedures).map((p) => p.bookingID)).filter(nullishFilter);
                    return bookingIDs.length;
                },
                placeOptions: (itineraryID) => {
                    const options = [];
                    const relatedProcedures = _r.itinerary.getters.procedures(itineraryID);
                    Object.values(relatedProcedures).forEach((procedure) => {
                        const placeAccountRefs = procedure.placeAccounts;
                        const placeAccountIDs = Object.keys(placeAccountRefs !== null && placeAccountRefs !== void 0 ? placeAccountRefs : {});
                        placeAccountIDs.forEach((id) => {
                            var _a;
                            const placeAccount = (_a = store.state.places.placeAccounts) === null || _a === void 0 ? void 0 : _a[id];
                            if (!(placeAccount === null || placeAccount === void 0 ? void 0 : placeAccount.privatePlaces))
                                return;
                            placeAccount === null || placeAccount === void 0 ? void 0 : placeAccount.privatePlaces.forEach((place) => {
                                options.push({
                                    place,
                                    placeAccount,
                                });
                            });
                        });
                    });
                    return options;
                },
                /**
                 * Determine if a given procedure in an itinerary has a drop-off or mail-in
                 * waypoint action but has no correlated patient action
                 */
                procedureIsDropOffOrphaned: (procedureID, itineraryID) => {
                    const procedure = extractTarget(procedureID, (id) => _r.procedures.getRaw(id));
                    if (!procedure)
                        return false;
                    const wpas = objectFilter(_r.itinerary.getters.currentWaypointActions(itineraryID), (wpa) => { var _a; return !!((_a = wpa.procedures) === null || _a === void 0 ? void 0 : _a[procedure.id]); });
                    const patientActionExists = objectSome(wpas, (waypointAction) => (waypointAction === null || waypointAction === void 0 ? void 0 : waypointAction.type) === WaypointActionType.PATIENT_ACTION);
                    const mailInOrDropOffExists = objectSome(wpas, (waypointAction) => (waypointAction === null || waypointAction === void 0 ? void 0 : waypointAction.type) === WaypointActionType.DROPOFF
                        || (waypointAction === null || waypointAction === void 0 ? void 0 : waypointAction.type) === WaypointActionType.MAIL_IN);
                    return !patientActionExists && mailInOrDropOffExists;
                },
                procedures: (itineraryID) => {
                    const i = extractTargetOrPriority(itineraryID, _r.itineraries.getRaw, itinerary);
                    if (!i)
                        return {};
                    return _r.itineraries.getters.procedures(i);
                },
                serviceRegionsFromCache: (itineraryID) => {
                    const i = extractTargetOrPriority(itineraryID, _r.itineraries.getRaw, itinerary);
                    if (!i)
                        return {};
                    return _r.itineraries.getters.serviceRegionsFromCache(i);
                },
                shift: (itineraryID) => {
                    const i = extractTargetOrPriority(itineraryID, _r.itineraries.getRaw, itinerary);
                    if (!i)
                        return null;
                    return _r.itineraries.getters.shift(i);
                },
                /** Used to identify the need to commit additions */
                someDraftChildExists: (itineraryID) => _r.itinerary.getters.currentWaypoints(itineraryID).some((wp) => hasStatus(wp, WaypointStatusName.DRAFT)
                    || _r.waypoints.getters.waypointActions(wp).some((wpa) => hasStatus(wpa, WaypointActionStatusName.DRAFT))),
                someDraftWaypointMismatched: (itineraryID) => {
                    const i = extractTargetOrPriority(itineraryID, _r.itineraries.getRaw, itinerary);
                    if (!i)
                        return false;
                    return _r.itineraries.getters.waypoints(i).some((wp) => _r.waypoints.getters.draftWaypointMismatched(wp));
                },
                timeZone: (itineraryID) => {
                    const i = extractTargetOrPriority(itineraryID, _r.itineraries.getRaw, itinerary);
                    if (!i)
                        return;
                    return _r.itineraries.getters.timeZone(i);
                },
                totalDuration: (itineraryID) => {
                    const i = extractTargetOrPriority(itineraryID, _r.itineraries.getRaw, itinerary);
                    if (!i)
                        return 0;
                    return _r.itineraries.getters.totalDuration(i);
                },
                waypointIndexByID: (waypointID, itineraryID) => _r.itinerary.getters.currentWaypoints(itineraryID).findIndex((waypoint) => waypoint.id === waypointID),
                /** Get the index of a waypoint from the level of the waypoint action */
                wpaParentIndex: (wpaID, itineraryID) => _r.itinerary.getters.currentWaypoints(itineraryID).findIndex((waypoint) => waypoint.waypointActions
                    && waypoint.waypointActions.findIndex(({ id }) => id === wpaID) !== -1),
            },
            /**
             * Setters for the itinerary.
             * Child setters are defined as an object generated by the keys of the
             * itinerary structure. This allows for interfacing with children of the
             * itinerary in a simple way.
             *
             * i.e itinerary.set.status(ItineraryStatusName.CREATED)
             *
             * To set the itinerary as a whole, use the `value` setter.
             * i.e itinerary.set.value(itinerary)
             */
            set: {
                /** Enables itinerary.set.{key}(val) */
                ...Object.keys(injectNulls(initItinerary())).reduce((acc, _key) => {
                    const key = _key;
                    acc[key] = (value, queueUpdate = false) => {
                        itinerary[key] = (value !== null && value !== void 0 ? value : null);
                        if (!queueUpdate)
                            return;
                        const label = typeof queueUpdate === 'string'
                            ? queueUpdate
                            : `set itinerary ${formatCamelCase(key).toLowerCase()}`;
                        useDbSyncStore().queueNextUpdate(getItineraryPath(itinerary.id, key), label, value, DbRef.ITINERARIES);
                    };
                    return acc;
                }, {}),
                value: (itineraryID) => {
                    Object.assign(itinerary, itineraryID);
                },
            },
            value: itinerary,
        },
        offices: recordManager()({}),
        orders: recordManager({
            context: DbRef.ITINERARIES,
            label: 'order',
            pathGetter: getOrderPath,
        })({
            /** All patient bookings */
            bookings: (order) => {
                var _a;
                const o = extractTarget(order, _r.orders.getRaw);
                if (!o)
                    return null;
                return objectFilter(objectMap((_a = o.bookings) !== null && _a !== void 0 ? _a : {}, (_, id) => _r.bookings.getRaw(id)), nullishFilter);
            },
            /** Allow falling back to denormalized order for legacy reasons */
            denormalized: (order) => {
                var _a;
                const denormalizedID = (typeof order === 'string') ? order : order.id;
                return (_a = _r.denormalizedOrders.getRaw(denormalizedID)) !== null && _a !== void 0 ? _a : null;
            },
            draftWaypoints: (order) => {
                const o = extractTarget(order, _r.orders.getRaw);
                if (!o)
                    return null;
                const procedures = _r.orders.getters.procedures(order);
                if (!procedures)
                    return null;
                const draftWaypointIDs = {
                    ...o.draftWaypoints,
                    ...objectMap(procedures, (_) => true, (_, p) => { var _a; return (_a = p.draftWaypointID) !== null && _a !== void 0 ? _a : ''; }),
                };
                return objectFilter(objectMap(draftWaypointIDs, (_, id) => _r.draftWaypoints.getRaw(id)), nullishFilter);
            },
            fulfillmentEvents: (order) => {
                var _a;
                const o = extractTarget(order, _r.orders.getRaw);
                const denormalized = (_a = _r.orders.getters.denormalized(order)) === null || _a === void 0 ? void 0 : _a.latestFulfillmentDate;
                const denFallback = denormalized
                    ? [formatDate(initTimeZoneDate(undefined, denormalized), 'MMM D, YYYY', dateParams)]
                    : undefined;
                if (!o)
                    return denFallback !== null && denFallback !== void 0 ? denFallback : null;
                const itineraries = _r.orders.getters.itineraries(order);
                if (!itineraries)
                    return denFallback !== null && denFallback !== void 0 ? denFallback : null;
                const fulfillmentEvents = Object.values(itineraries).map((i) => { var _a; return formatDate((_a = i.dateInfo) === null || _a === void 0 ? void 0 : _a.date, 'MMM D, YYYY', dateParams); });
                return fulfillmentEvents.length ? fulfillmentEvents : denFallback !== null && denFallback !== void 0 ? denFallback : null;
            },
            fulfillmentStatuses: (order) => {
                var _a, _b, _c;
                const denormalized = _r.orders.getters.denormalized(order);
                const getDisplayStatus = (draftWaypoint) => {
                    var _a, _b, _c, _d, _e;
                    const status = getWaypointStatusFromInfoTitle((_a = draftWaypoint.statusInfo) === null || _a === void 0 ? void 0 : _a.title);
                    if (hasStatus(status, DraftWaypointStatus.CANCELED)
                        || hasStatus(draftWaypoint, DraftWaypointStatus.CANCELED)) {
                        return {
                            id: draftWaypoint.id,
                            status: DraftWaypointStatus.CANCELED,
                        };
                    }
                    if (draftWaypoint.waypointID) {
                        const waypointStatus = (_c = (_b = _r.waypoints.getRaw(draftWaypoint.waypointID)) === null || _b === void 0 ? void 0 : _b.status.status) !== null && _c !== void 0 ? _c : (_e = (_d = denormalized === null || denormalized === void 0 ? void 0 : denormalized.waypointStatuses) === null || _d === void 0 ? void 0 : _d.find(({ id }) => id === draftWaypoint.waypointID)) === null || _e === void 0 ? void 0 : _e.status;
                        if (waypointStatus) {
                            return {
                                id: draftWaypoint.id,
                                status: waypointStatus,
                            };
                        }
                    }
                    if (!status)
                        return null;
                    return {
                        id: draftWaypoint.id,
                        status,
                    };
                };
                const denValue = (Object.values((_a = denormalized === null || denormalized === void 0 ? void 0 : denormalized.draftWaypoints) !== null && _a !== void 0 ? _a : {})
                    .map(getDisplayStatus)
                    .filter(nullishFilter));
                const o = extractTarget(order, _r.orders.getRaw);
                if (!o)
                    return denValue !== null && denValue !== void 0 ? denValue : null;
                const draftWaypoints = _r.orders.getters.draftWaypoints(order);
                if (!draftWaypoints)
                    return denValue !== null && denValue !== void 0 ? denValue : null;
                const statuses = objectMap(draftWaypoints, (dw, id) => {
                    var _a, _b;
                    const denormalizedWaypoint = denormalized === null || denormalized === void 0 ? void 0 : denormalized.draftWaypoints[id];
                    const denFallback = denormalizedWaypoint
                        ? getDisplayStatus(denormalizedWaypoint)
                        : null;
                    const dwStatus = (_b = (_a = getDisplayStatus(dw)) === null || _a === void 0 ? void 0 : _a.status) !== null && _b !== void 0 ? _b : dw.status;
                    return {
                        id,
                        status: dwStatus !== null && dwStatus !== void 0 ? dwStatus : denFallback === null || denFallback === void 0 ? void 0 : denFallback.status,
                    };
                });
                return (_c = (_b = Object.values(statuses).filter(nullishFilter)) !== null && _b !== void 0 ? _b : denValue) !== null && _c !== void 0 ? _c : null;
            },
            itineraries: (order) => {
                const o = extractTarget(order, _r.orders.getRaw);
                if (!o)
                    return null;
                const itineraryIDs = _r.orders.getters.itineraryIDs(order);
                if (!itineraryIDs)
                    return null;
                const mapped = itineraryIDs
                    .map((id) => {
                    const it = _r.itineraries.getRaw(id);
                    if (!it)
                        return [id, null];
                    return [id, it];
                });
                const itineraries = objectFilter(Object.fromEntries(mapped), nullishFilter);
                if (!Object.keys(itineraries).length)
                    return null;
                return itineraries;
            },
            itineraryIDs: (order) => {
                const o = extractTarget(order, _r.orders.getRaw);
                if (!o)
                    return null;
                const draftWaypoints = _r.orders.getters.draftWaypoints(order);
                const procedures = _r.orders.getters.procedures(order);
                const itineraryIDs = new Set();
                Object.values(draftWaypoints !== null && draftWaypoints !== void 0 ? draftWaypoints : {}).forEach((dw) => {
                    const waypoint = _r.draftWaypoints.getters.waypoint(dw);
                    if (waypoint === null || waypoint === void 0 ? void 0 : waypoint.itineraryID)
                        itineraryIDs.add(waypoint.itineraryID);
                });
                Object.values(procedures !== null && procedures !== void 0 ? procedures : {}).forEach((p) => {
                    const waypoints = _r.procedures.getters.waypoints(p);
                    if (!waypoints)
                        return;
                    Object.values(waypoints).forEach((wp) => {
                        if (wp.itineraryID)
                            itineraryIDs.add(wp.itineraryID);
                    });
                });
                return Array.from(itineraryIDs);
            },
            nurseNames: (order) => {
                const o = extractTarget(order, _r.orders.getRaw);
                if (!o)
                    return null;
                const procedures = _r.orders.getters.procedures(order);
                if (!procedures)
                    return null;
                const nurseNames = new Set(Object.values(procedures)
                    .map((p) => { var _a; return _r.users.getters.fullName((_a = p.nurseID) !== null && _a !== void 0 ? _a : ''); })
                    .filter(nullishFilter));
                if (!nurseNames.size)
                    return null;
                return Array.from(nurseNames);
            },
            patientNames: (order) => {
                const patients = _r.orders.getters.patients(order);
                if (!patients)
                    return null;
                return patients.map((p) => _r.users.getters.fullName(p))
                    .filter(nullishFilter);
            },
            patients: (order) => {
                const o = extractTarget(order, _r.orders.getRaw);
                if (!o)
                    return null;
                const bookings = _r.orders.getters.bookings(order);
                if (!bookings)
                    return null;
                const patients = Object.values(bookings).map((b) => _r.bookings.getters.patient(b)).filter(nullishFilter);
                return patients;
            },
            prescriber: (order) => {
                const o = extractTarget(order, _r.orders.getRaw);
                if (!(o === null || o === void 0 ? void 0 : o.prescriberID))
                    return null;
                return _r.users.getRaw(o.prescriberID);
            },
            prescriberName: (order) => {
                var _a, _b;
                const denValue = (_b = (_a = _r.orders.getters.denormalized(order)) === null || _a === void 0 ? void 0 : _a.prescriber) === null || _b === void 0 ? void 0 : _b.formattedName;
                const o = extractTarget(order, _r.orders.getRaw);
                if (!(o === null || o === void 0 ? void 0 : o.prescriberID))
                    return denValue !== null && denValue !== void 0 ? denValue : null;
                const prescriber = _r.orders.getters.prescriber(order);
                if (!prescriber)
                    return denValue !== null && denValue !== void 0 ? denValue : null;
                return formatFullName(prescriber, false, true);
            },
            procedures: (order) => {
                const o = extractTarget(order, _r.orders.getRaw);
                if (!o)
                    return null;
                const bookings = _r.orders.getters.bookings(order);
                if (!bookings)
                    return null;
                const allProcedures = objectReduce(bookings, (procedures, [_, b]) => {
                    const bookingProcedures = _r.bookings.getters.procedures(b);
                    if (!bookingProcedures)
                        return procedures;
                    return ({ ...procedures, ...bookingProcedures });
                }, {});
                return allProcedures;
            },
            waypointIDs: (order) => {
                const o = extractTarget(order, _r.orders.getRaw);
                if (!o)
                    return null;
                const procedures = _r.orders.getters.procedures(order);
                const draftWaypoints = _r.orders.getters.draftWaypoints(order);
                const waypointIDs = new Set();
                Object.values(draftWaypoints !== null && draftWaypoints !== void 0 ? draftWaypoints : {}).forEach((dw) => {
                    const waypoints = dw.waypointID;
                    if (waypoints)
                        waypointIDs.add(waypoints);
                });
                Object.values(procedures !== null && procedures !== void 0 ? procedures : {}).forEach((p) => {
                    const waypoints = _r.procedures.getters.waypoints(p);
                    if (!waypoints)
                        return;
                    Object.values(waypoints).forEach((wp) => waypointIDs.add(wp.id));
                });
                return Array.from(waypointIDs);
            },
            waypoints: (order) => {
                const o = extractTarget(order, _r.orders.getRaw);
                if (!o)
                    return null;
                const waypointIDs = _r.orders.getters.waypointIDs(order);
                if (!waypointIDs)
                    return null;
                const waypoints = Object.fromEntries(waypointIDs.map((id) => {
                    const waypoint = _r.waypoints.getRaw(id);
                    if (!waypoint)
                        return [id, null];
                    return [id, waypoint];
                }));
                return objectFilter(waypoints, nullishFilter);
            },
        }),
        procedures: recordManager({
            context: DbRef.ITINERARIES,
            label: 'procedure',
            pathGetter: getProcedurePath,
        })({
            booking: (procedure) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return;
                const { bookingID } = p;
                if (!bookingID)
                    return;
                return store.state.bookings.bookings[bookingID];
            },
            childWorkflows: (procedure) => {
                var _a;
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return {};
                const samples = Object.values((_a = p.samples) !== null && _a !== void 0 ? _a : {});
                if (!samples.length)
                    return {};
                const childWorkflows = samples.map((s) => {
                    const workflows = _r.samples.getters.workflows(s);
                    if (!workflows)
                        return null;
                    return [s.id, workflows[p.id]];
                }).filter(nullishFilter);
                return Object.fromEntries(childWorkflows);
            },
            dateTimeInfo: (procedure) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!(p === null || p === void 0 ? void 0 : p.dateInfo))
                    return;
                return getProcedureDateTimeInfo(p);
            },
            draftWaypoint: (procedure) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return null;
                const { draftWaypointID } = p;
                if (!draftWaypointID)
                    return null;
                return _r.draftWaypoints.getRaw(draftWaypointID);
            },
            extendedSamples: (procedure) => {
                var _a;
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return [];
                const samples = Object.values((_a = p === null || p === void 0 ? void 0 : p.samples) !== null && _a !== void 0 ? _a : {});
                if (!samples.length)
                    return [];
                const extendedSamplesByID = samples.map(getExtendedSample);
                const extendedSamples = extendedSamplesByID.filter(nullishFilter);
                return extendedSamples.sort((a, b) => (a.orderWeight < b.orderWeight) ? 1 : -1);
            },
            formattedAddress: (procedure) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!(p === null || p === void 0 ? void 0 : p.location))
                    return;
                return formatAddressUS(p.location);
            },
            formattedContactInfo: (procedure) => {
                var _a, _b;
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!(p === null || p === void 0 ? void 0 : p.contact))
                    return;
                if (p.contact.userID) {
                    const { userID } = p.contact;
                    /** the contact could be a staff member so we should check that as well */
                    const user = (_b = (_a = _r.users.getRaw(userID)) !== null && _a !== void 0 ? _a : store.state.users.users[userID]) !== null && _b !== void 0 ? _b : store.state.users.staffUsers[userID];
                    const userFullName = formatFullName(user);
                    if (!(user === null || user === void 0 ? void 0 : user.info))
                        return userFullName;
                    const { phone } = user === null || user === void 0 ? void 0 : user.info;
                    const formattedPhone = formatPhoneNumber(phone);
                    return `${userFullName} - ${formattedPhone}`;
                }
                return `${p.contact.name} - ${formatPhoneNumber(p.contact.phone)}`;
            },
            formattedLabInformation: (procedure) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return;
                const infoItems = [];
                const addPlaceAccountToList = ({ id }) => {
                    var _a;
                    const placeAccount = (_a = store.state.places.placeAccounts) === null || _a === void 0 ? void 0 : _a[id];
                    if (!placeAccount)
                        return;
                    let accountDetails = `${placeAccount.name}`;
                    if (placeAccount.account)
                        accountDetails = `${accountDetails} (${placeAccount.account})`;
                    if (placeAccount.noAccount)
                        accountDetails = 'No account';
                    const placeGroup = store.getters['places/getPlaceGroupByPlaceAccount'](placeAccount);
                    if (!placeGroup)
                        return accountDetails;
                    infoItems.push(`${placeGroup.name}: ${accountDetails}`);
                };
                if (p.placeAccounts) {
                    const { placeAccounts } = p;
                    objectMap(placeAccounts, addPlaceAccountToList);
                    return buildBulletSeparatedString(infoItems);
                }
                return buildBulletSeparatedString(infoItems);
            },
            formattedNurseName: (procedure) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return;
                const nurse = _r.procedures.getters.nurse(p);
                if (!nurse)
                    return;
                return formatFullName(nurse, true, false);
            },
            friendlyStatus: (procedure) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return;
                const waypoint = _r.procedures.getters.patientWaypoint(p);
                if (!waypoint)
                    return;
                return 'Next task';
            },
            isMailInOnly: (procedure) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return false;
                const { supplyInstructionConfig } = p;
                return (supplyInstructionConfig === null || supplyInstructionConfig === void 0 ? void 0 : supplyInstructionConfig.samplesDeliveryType) === SamplesDeliveryType.MAIL_IN;
            },
            itineraryID: (procedure) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return;
                const waypoint = _r.procedures.getters.patientWaypoint(p);
                if (!waypoint)
                    return;
                return waypoint.itineraryID;
            },
            labName: (procedure) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return;
                const { dropOffPlaceGroups } = p;
                if (!dropOffPlaceGroups)
                    return;
                const labPlaceGroupID = Object.keys(dropOffPlaceGroups)[0];
                if (!labPlaceGroupID)
                    return;
                const labPlaceGroup = _r[DbRef.PLACE_GROUPS].getRaw(labPlaceGroupID);
                if (!labPlaceGroup)
                    return;
                return labPlaceGroup.name;
            },
            nurse: (procedure) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return;
                const { nurseID } = p;
                if (!nurseID)
                    return;
                return store.state.users.users[nurseID];
            },
            nurseInstructions: (procedure) => {
                var _a;
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return undefined;
                return (_a = p.instructions) === null || _a === void 0 ? void 0 : _a.filter((instruction) => instruction.type === InstructionType.NURSE);
            },
            /** Offices may override kitSupplyID for particular tasks */
            officeKitSupplyID: (p) => {
                var _a;
                const procedure = extractTarget(p, _r.procedures.getRaw);
                if (!procedure)
                    return;
                return (_a = getStore().state.supplies
                    .proceduresOfficeSupplyConfig[procedure.id]) === null || _a === void 0 ? void 0 : _a.supplyConfig.kitSupplyID;
            },
            order: (procedure) => {
                var _a;
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return;
                const { orderID } = p;
                if (!orderID)
                    return;
                return (_a = _r.orders.getRaw(orderID)) !== null && _a !== void 0 ? _a : undefined;
            },
            patient: (procedure) => {
                var _a, _b;
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return;
                const { bookingID } = p;
                if (!bookingID)
                    return;
                const booking = (_a = _r.bookings.getRaw(bookingID)) !== null && _a !== void 0 ? _a : store.state.bookings.bookings[bookingID];
                if (!booking)
                    return;
                let { patientID } = booking;
                if (isEncrypted(patientID)) {
                    patientID = getEncryptionCachedData(patientID.id) || patientID;
                }
                const patient = (_b = _r.users.getRaw(patientID)) !== null && _b !== void 0 ? _b : store.state.users.users[patientID];
                if (!patient)
                    return;
                return patient;
            },
            patientDoB: (procedure) => {
                var _a;
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return;
                const patient = _r.procedures.getters.patient(p);
                if (!patient)
                    return;
                return formatDateOfBirth((_a = patient.info) === null || _a === void 0 ? void 0 : _a.dateOfBirth);
            },
            patientName: (procedure) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return;
                const patient = _r.procedures.getters.patient(p);
                if (!patient)
                    return;
                return formatFullName(patient, true, false);
            },
            patientWaypoint: (procedure) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return;
                const waypoints = _r.procedures.getters.waypoints(p);
                if (!waypoints)
                    return;
                const patientWaypoint = Object.values(waypoints)
                    .find((wp) => wp.type === WaypointType.PATIENT);
                if (!patientWaypoint)
                    return;
                return patientWaypoint;
            },
            prescriber: (procedure) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return;
                const prescriberID = _r.procedures.getters.prescriberID(p);
                if (!prescriberID)
                    return;
                return store.state.users.users[prescriberID];
            },
            prescriberID: (procedure) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return;
                const { orderID } = p;
                if (!orderID)
                    return;
                const order = store.state.orders.orders[orderID];
                if (!order)
                    return;
                return order.prescriberID;
            },
            prescriberName: (procedure) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return;
                const prescriber = _r.procedures.getters.prescriber(p);
                if (!prescriber)
                    return;
                return formatFullName(prescriber, true, true);
            },
            scheduledTime: (procedure) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return;
                const waypoint = _r.procedures.getters.patientWaypoint(p);
                if (!waypoint)
                    return;
                return formatTime(waypoint.date, 'MMM D', 'h:mm a');
            },
            sortedWaypointActions: (procedure) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return [];
                const waypointActions = _r.procedures.getters.waypointActions(p.id);
                if (!waypointActions)
                    return [];
                return Object.values(waypointActions).sort((a, b) => {
                    if (!a.waypointID || !b.waypointID)
                        return 0;
                    const indexA = _r.itinerary.getters.waypointIndexByID(a.waypointID);
                    const indexB = _r.itinerary.getters.waypointIndexByID(b.waypointID);
                    return indexA - indexB;
                });
            },
            successWorkflows: (procedure) => _r.procedures.getters.workflowsByExecutionType(procedure),
            supplyConfigItems: (procedure) => {
                var _a;
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!(p === null || p === void 0 ? void 0 : p.supplyInstructionConfig))
                    return null;
                const supplyGetter = store.getters['variables/getSupplyByID'];
                const supplies = Object.values((_a = p.supplyInstructionConfig.supplyInstructions) !== null && _a !== void 0 ? _a : {})
                    .map((instruction) => {
                    var _a, _b;
                    return ({
                        name: ((_b = (_a = supplyGetter(instruction.supplyID)) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : '').replace('CareSend ', ''),
                        quantity: instruction.quantity,
                    });
                })
                    .filter(({ name }) => !!name)
                    .map(({ quantity, name }) => `${quantity} ${name}`);
                if (!isNullish(supplies))
                    return null;
                return supplies;
            },
            waypointActions: (procedure) => {
                var _a;
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return null;
                let wpas = objectFilter(objectMap((_a = p.waypointActions) !== null && _a !== void 0 ? _a : {}, (_, id) => _r.waypointActions.getRaw(id)), nullishFilter);
                if (Object.keys(wpas).length)
                    return wpas;
                wpas = _r.waypointActions.filterRaw((wpa) => { var _a; return !!((_a = wpa === null || wpa === void 0 ? void 0 : wpa.procedures) === null || _a === void 0 ? void 0 : _a[p.id]); });
                return wpas;
            },
            waypointFriendlyStatus: (procedure) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return;
                const waypoint = _r.procedures.getters.patientWaypoint(p);
                if (!waypoint)
                    return;
                const waypointComplete = hasStatus(waypoint, WaypointStatusName.COMPLETE);
                const locked = _r.waypoints.getters.waypointIsLocked(waypoint.id);
                if (waypointComplete)
                    return 'Complete';
                if (!locked && !waypointComplete)
                    return 'Next task';
                return 'Fulfillment queue';
            },
            /** Get the waypoint ID of the patient waypoint action */
            waypoints: (p) => {
                const procedure = extractTarget(p, _r.procedures.getRaw);
                if (!procedure)
                    return;
                const waypointActions = _r.procedures.getters.waypointActions(p);
                if (!waypointActions)
                    return;
                const waypointIDs = objectFilter(objectMap(waypointActions, (wpa) => { var _a; return (_a = _r.waypointActions.getRaw(wpa.id)) === null || _a === void 0 ? void 0 : _a.waypointID; }), nullishFilter);
                const waypoints = objectFilter(objectMap(waypointIDs, (id) => _r.waypoints.getRaw(id)), nullishFilter);
                return waypoints;
            },
            workflowContexts: (procedure) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return undefined;
                const { waypointActions } = p;
                if (!waypointActions)
                    return undefined;
                const workflowContexts = new Set();
                Object.keys(waypointActions).forEach((wpaID) => {
                    const workflows = _r.waypointActions.getters.workflows(wpaID);
                    if (!workflows)
                        return;
                    Object.values(workflows).forEach((wfByParent) => {
                        Object.keys(wfByParent).forEach((context) => {
                            workflowContexts.add(context);
                        });
                    });
                });
                return workflowContexts;
            },
            workflows: (procedure) => _r.procedures.getters.workflowsByExecutionType(procedure),
        }, {
            chartingItemByIndexArray: (p, indexArray) => {
                var _a;
                const procedure = extractTarget(p, _r.procedures.getRaw);
                if (!procedure)
                    return;
                const chartingItems = (_a = procedure.charting) === null || _a === void 0 ? void 0 : _a.items;
                if (!chartingItems)
                    return;
                return getChartingItemByIndexArray(chartingItems, indexArray);
            },
            dropoffPlaceGroup: (p) => {
                var _a;
                const procedure = extractTarget(p, _r.procedures.getRaw);
                if (!procedure)
                    return null;
                // At this point, there should only be one dropoff place group
                const [dropoffPlaceGroupID] = Object.keys((_a = procedure.dropOffPlaceGroups) !== null && _a !== void 0 ? _a : {});
                if (!dropoffPlaceGroupID)
                    return null;
                return _r[DbRef.PLACE_GROUPS].getRaw(dropoffPlaceGroupID);
            },
            // TODO: FINISH THIS FUNC
            getChartingChecklistItem: (procedureID, indexArray, checkboxIndex) => {
                var _a;
                const procedure = _r.procedures.getRaw(procedureID);
                if (!procedure)
                    return;
                const chartingItemByIndexArray = _r.procedures.getters.chartingItemByIndexArray(procedure, indexArray);
                if (!chartingItemByIndexArray)
                    return;
                return (_a = chartingItemByIndexArray.checklist) === null || _a === void 0 ? void 0 : _a[checkboxIndex];
            },
            initialScannableKitSupply: (p, offline) => {
                const kitSupplyID = _r.procedures.getters.kitSupplyID(p, offline);
                if (!kitSupplyID)
                    return;
                const supply = getStore().getters['variables/getSupplyByID'](kitSupplyID);
                if (!supply)
                    return;
                return {
                    ...supply,
                    isScanned: false,
                    key: generateID(),
                };
            },
            kitSupplyID: (p, offline) => {
                var _a;
                const procedure = extractTarget(p, _r.procedures.getRaw);
                if (!procedure)
                    return;
                if (offline)
                    return _r.procedures.getters.taskKitSupplyID(procedure, offline);
                return (_a = _r.procedures.getters.officeKitSupplyID(procedure)) !== null && _a !== void 0 ? _a : _r.procedures.getters.taskKitSupplyID(procedure);
            },
            scannableItemsByWorkflowItemID: (p) => {
                const procedure = extractTarget(p, _r.procedures.getRaw);
                if (!procedure)
                    return {};
                return {
                    [DropOffWorkflowItemID.SCAN_SPECIMEN_BAG]: getDropOffScanSpecimenBag(procedure, _r.procedures.getters.patientName(p)),
                    [PackingWorkflowItemID.SCAN_KIT_SUPPLY_ITEMS]: getInitialProcedureKitSupply(procedure, procedure.disableSampleScanning),
                };
            },
            taskKitSupplyID: (p, offline) => {
                var _a;
                const procedure = extractTarget(p, _r.procedures.getRaw);
                if (!procedure)
                    return;
                const { taskID } = procedure;
                const key = offline ? 'offlineKitSupplyID' : 'kitSupplyID';
                return (_a = getStore().getters['variables/getTaskByID'](taskID)) === null || _a === void 0 ? void 0 : _a[key];
            },
            workflowScanTitle: (p, supply) => {
                var _a;
                const procedure = extractTarget(p, _r.procedures.getRaw);
                if (!procedure)
                    return 'Scan Samples';
                const disablePatientVisitSupplyScanning = (_a = procedure.disableSampleScanning) !== null && _a !== void 0 ? _a : store.getters['variables/getFeatureFlagByName'](FeatureFlag.DISABLE_PATIENT_VISIT_SUPPLY_SCANNING);
                if (disablePatientVisitSupplyScanning)
                    return 'Click on the supplies below to activate them.';
                const kitSupplyItem = supply !== null && supply !== void 0 ? supply : procedure.kitSupplyItem;
                const procedureBagLast4 = (kitSupplyItem === null || kitSupplyItem === void 0 ? void 0 : kitSupplyItem.supplyID)
                    ? kitSupplyItem === null || kitSupplyItem === void 0 ? void 0 : kitSupplyItem.supplyID.slice(-4)
                    : null;
                const procedureBagSuffix = procedureBagLast4
                    ? ` ending in "${procedureBagLast4.toUpperCase()}"`
                    : '';
                return 'Scan the following items and pack them into the procedure bag'
                    + `${procedureBagSuffix}`;
            },
            workflowsByExecutionType: (procedure, execType) => {
                const p = extractTarget(procedure, _r.procedures.getRaw);
                if (!p)
                    return {};
                const { waypointActions } = p;
                if (!waypointActions)
                    return {};
                const allWorkflows = objectMap(waypointActions, ({ id: wpaID }) => {
                    const wpaWorkflows = _r.waypointActions.getters.workflows(wpaID);
                    const contexts = new Set(Object.values(wpaWorkflows !== null && wpaWorkflows !== void 0 ? wpaWorkflows : {})
                        .map((wf) => Object.values(wf).flat().map(({ recordMeta: { childWorkflow } }) => {
                        var _a, _b;
                        if (execType === 'failure')
                            return [childWorkflow === null || childWorkflow === void 0 ? void 0 : childWorkflow.failureContext];
                        if (execType === 'success')
                            return [childWorkflow === null || childWorkflow === void 0 ? void 0 : childWorkflow.context];
                        if (!(childWorkflow === null || childWorkflow === void 0 ? void 0 : childWorkflow.context))
                            return [];
                        const workflow = (_b = (_a = p.workflows) === null || _a === void 0 ? void 0 : _a[wpaID]) === null || _b === void 0 ? void 0 : _b[childWorkflow === null || childWorkflow === void 0 ? void 0 : childWorkflow.context];
                        const isFailed = workflow && workflowUtils.getIsWorkflowFailed(workflow, undefined, 'partial');
                        if (!isFailed)
                            return [childWorkflow === null || childWorkflow === void 0 ? void 0 : childWorkflow.context];
                        return [childWorkflow === null || childWorkflow === void 0 ? void 0 : childWorkflow.context, childWorkflow === null || childWorkflow === void 0 ? void 0 : childWorkflow.failureContext];
                    })).flat().flat()
                        .filter(nullishFilter));
                    const workflows = Object.fromEntries(Array.from(contexts).map((context) => [
                        context, getProcedureWorkflow(p, context, wpaID, _r),
                    ]));
                    return workflows;
                });
                return allWorkflows;
            },
        }),
        /**
         * We provide a custom path getter to properly comply with the nested
         * structure of samples within procedures.
         */
        samples: recordManager({
            context: 'samples',
            label: 'sample',
            pathGetter: (id, childPath, item) => {
                if (!item)
                    return id;
                const { procedureID } = item;
                return getProcedureSamplePath(procedureID, id, childPath);
            },
        })({
            workflows: (s) => {
                const sample = extractTarget(s, _r.samples.getRaw);
                if (!sample)
                    return {};
                return Object.fromEntries([
                    [sample.procedureID, {
                            [SampleWorkflowContextID.DEFAULT]: getSampleWorkflow(sample, SampleWorkflowContextID.DEFAULT, sample.procedureID, _r),
                        }],
                ]);
            },
        }),
        serviceRegionCache: recordManager({
            context: 'serviceRegionCache',
            persist: true,
        })({}),
        [DbRef.PLACES]: recordManager({
            context: DbRef.ITINERARIES,
            label: 'place',
            pathGetter: getPlacePath,
        })({}),
        [DbRef.PLACE_ACCOUNTS]: recordManager({
            context: DbRef.ITINERARIES,
            label: 'placeAccount',
            pathGetter: (id) => `${DbRef.PLACE_ACCOUNTS}/${id}`,
        })({}),
        [DbRef.PLACE_GROUPS]: recordManager({
            context: DbRef.ITINERARIES,
            label: 'placeGroup',
            pathGetter: getPlaceGroupPath,
        })({}),
        [DbRef.PLACE_GROUP_TYPES]: recordManager({
            context: DbRef.ITINERARIES,
            label: 'placeGroupType',
            pathGetter: (id, childPath) => `placeGroupTypes/${id}${childPath ? `/${childPath}` : ''}`,
        })({}),
        serviceRegionPolygons: recordManager({
            context: 'serviceRegionPolygons',
            pathGetter: (id) => `${DbRef.POLYGONS_BY_SERVICE_REGION}/${id}`,
            persist: true,
        })({}),
        serviceRegions: recordManager({
            context: DbRef.ITINERARIES,
            label: 'serviceRegion',
            pathGetter: (id) => `serviceRegions/${id}`,
        })({}),
        shifts: recordManager({
            context: DbRef.ITINERARIES,
            label: 'shift',
            pathGetter: getShiftPath,
        })({
            areaOfFirstStop: (shift) => {
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return;
                const firstWaypoint = _r.shifts.getters.firstWaypoint(shift);
                if (!firstWaypoint)
                    return;
                const { location } = firstWaypoint;
                const city = getCityByLocation(location);
                const zipCode = getPostalCodeByLocation(location);
                const neighborhood = getNeighborhoodByLocation(location);
                const areaName = city !== null && city !== void 0 ? city : neighborhood;
                if (!areaName || !zipCode)
                    return;
                return `${areaName}, ${zipCode}`;
            },
            assignedNurse: (shiftID) => {
                var _a;
                const s = extractTarget(shiftID, _r.shifts.getRaw);
                if (!(s === null || s === void 0 ? void 0 : s.userID))
                    return;
                return (_a = store.state.users.users) === null || _a === void 0 ? void 0 : _a[s.userID];
            },
            deliveryStatus: (shift) => {
                const itineraries = _r.shifts.getters.itineraries(shift);
                const allItinerariesComplete = Object.values(itineraries).every((i) => hasStatus(i, ItineraryStatus.COMPLETED));
                if (allItinerariesComplete)
                    return 'Complete';
                const nextActionableItinerary = _r.shifts.getters.nextActionableItinerary(shift);
                if (!nextActionableItinerary)
                    return;
                const { status } = nextActionableItinerary;
                return status === ItineraryStatus.IN_PROGRESS ? 'In progress' : status;
            },
            detailsHeaderTitle: (shift) => {
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return '';
                const formattedDate = _r.shifts.getters.formattedDate(shift);
                return `Job for ${formattedDate}`;
            },
            firstPlaceGroupID: (shift) => {
                var _a;
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return;
                const firstProcedure = _r.shifts.getters.procedures(shift)[0];
                if (!firstProcedure)
                    return;
                const placeGroupID = Object.keys((_a = firstProcedure.dropOffPlaceGroups) !== null && _a !== void 0 ? _a : {})[0];
                return placeGroupID;
            },
            firstWaypoint: (shift) => {
                const itineraries = _r.shifts.getters.itineraries(shift);
                const firstItinerary = Object.values(itineraries)[0];
                if (!firstItinerary)
                    return;
                const waypoints = _r.itineraries.getters.waypoints(firstItinerary);
                return Object.values(waypoints)[0];
            },
            formattedDate: (shift) => {
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return '';
                const { startTime } = s;
                const currentYear = dayjs().year();
                if (dayjs(startTime.timestamp).year() === currentYear) {
                    return formatDate(startTime, 'MMM D', dateParams);
                }
                return formatDate(startTime, 'MMM D, YYYY', dateParams);
            },
            formattedDateReceived: (shift) => {
                var _a, _b;
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return;
                const procedures = _r.shifts.getters.procedures(s);
                const firstProcedure = Object.values(procedures)[0];
                if (!firstProcedure)
                    return;
                const { creationTimestamp } = firstProcedure;
                const tzDate = {
                    timeZone: (_b = (_a = s.startTime) === null || _a === void 0 ? void 0 : _a.timeZone) !== null && _b !== void 0 ? _b : dayjs.tz.guess(),
                    timestamp: creationTimestamp,
                };
                const currentYear = dayjs().year();
                if (dayjs(tzDate.timestamp).year() === currentYear) {
                    return formatDate(tzDate, 'MMM D', dateParams);
                }
                return formatDate(tzDate, 'MMM D, YYYY', dateParams);
            },
            formattedDuration: (shift) => {
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return;
                const itineraries = _r.shifts.getters.itineraries(shift);
                const totalDuration = Object.values(itineraries).reduce((total, next) => total + next.duration, 0);
                if (!totalDuration)
                    return;
                return displayMinutes(totalDuration);
            },
            formattedEndTime: (shift) => {
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return '';
                const { endTime } = s;
                return formatDate(endTime, 'h:mm a');
            },
            formattedNurseName: (shift) => {
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return '';
                const nurse = _r.shifts.getters.assignedNurse(shift);
                if (!nurse)
                    return '';
                return formatFullName(nurse, false, false);
            },
            formattedRange: (shift) => {
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return;
                const startTimeFormat = dayjs(s.startTime.timestamp).minute() === 0
                    ? 'h'
                    : 'h:mm';
                const endTimeFormat = dayjs(s.endTime.timestamp).minute() === 0
                    ? 'h a'
                    : 'h:mm a';
                /** If the start and end times are in different AM/PM periods, show the AM/PM */
                const showStartTimeAMPM = dayjs(s.startTime.timestamp).format('a')
                    !== dayjs(s.endTime.timestamp).format('a');
                /** If the user is in a different timezone than the shift, show the timezone */
                const showTimeZone = dayjs.tz.guess() !== s.startTime.timeZone;
                const startTime = dayjs(s.startTime.timestamp)
                    .tz(s.startTime.timeZone)
                    .format(`${startTimeFormat}`
                    + ` ${showStartTimeAMPM ? 'a' : ''}`);
                const endTime = dayjs(s.endTime.timestamp)
                    .tz(s.endTime.timeZone)
                    .format(`${endTimeFormat}`
                    + ` ${showTimeZone ? 'z' : ''}`);
                if (!startTime || !endTime)
                    return;
                return `${startTime} - ${endTime}`;
            },
            formattedStartTime: (shift) => {
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return '';
                const { startTime } = s;
                return formatDate(startTime, 'h:mm a');
            },
            isPast: (shift) => {
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return false;
                return dayjs(s.endTime.timestamp).isBefore(dayjs());
            },
            itineraries: (shift) => {
                var _a;
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return {};
                return objectFilter(objectMap((_a = s.itineraries) !== null && _a !== void 0 ? _a : {}, (_, id) => _r.itineraries.getRaw(id)), nullishFilter);
            },
            nextActionableItinerary: (shift) => {
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return;
                const itineraries = _r.shifts.getters.itineraries(shift);
                const nextItinerary = Object.values(itineraries).find((i) => !hasStatus(i, ItineraryStatus.ACCEPTED)
                    || !hasStatus(i, ItineraryStatus.CREATED)
                    || !hasStatus(i, ItineraryStatus.IN_PROGRESS));
                return nextItinerary;
            },
            postalCodes: (shift) => {
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return [];
                const { postalCodes } = _r.shifts.getters.stateAndPostalCodes(shift);
                if (!postalCodes)
                    return [];
                return postalCodes;
            },
            procedures: (shift) => {
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return [];
                const itineraries = _r.shifts.getters.itineraries(shift);
                const procedures = Object.values(itineraries).reduce((acc, next) => {
                    const itineraryProcedures = _r.itineraries.getters.procedures(next);
                    return [...acc, ...Object.values(itineraryProcedures)];
                }, []);
                return procedures;
            },
            productNamesAndInfoURLs: (shift) => {
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return {};
                const products = _r.shifts.getters.products(shift);
                const placeGroupID = _r.shifts.getters.firstPlaceGroupID(shift);
                if (!placeGroupID)
                    return {};
                return products.reduce((acc, p) => {
                    var _a;
                    const productPlaceGroupConfig = (_a = p.placeGroups) === null || _a === void 0 ? void 0 : _a[placeGroupID];
                    if (!productPlaceGroupConfig)
                        return acc;
                    const productName = productPlaceGroupConfig.friendlyName;
                    const productInfoURL = productPlaceGroupConfig.infoURL;
                    if (!productName)
                        return acc;
                    return { ...acc, [productName]: productInfoURL };
                }, {});
            },
            products: (shift) => {
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return [];
                const procedures = _r.shifts.getters.procedures(shift);
                const productIDs = Object.values(procedures).map((p) => { var _a; return Object.keys((_a = p.products) !== null && _a !== void 0 ? _a : {}); }).flat();
                const products = productIDs.map((id) => store.getters['variables/getProductByID'](id));
                return products.filter(nullishFilter);
            },
            requiredEquipment: () => 'Phlebotomy supplies',
            /** For nurses, returns the office their offer was made from */
            selfOfferedBy: (shift) => {
                var _a, _b;
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return;
                const { id: userID } = (_a = store.state.auth.user) !== null && _a !== void 0 ? _a : {};
                const shiftOffer = (_b = s.nursesOffered) === null || _b === void 0 ? void 0 : _b[userID !== null && userID !== void 0 ? userID : ''];
                if (!(shiftOffer === null || shiftOffer === void 0 ? void 0 : shiftOffer.staffingOfficeID))
                    return;
                const staffingOffice = _r.offices.getRaw(shiftOffer.staffingOfficeID);
                if (!staffingOffice)
                    return;
                const isStaffingOffice = (o) => (o === null || o === void 0 ? void 0 : o.type) === OfficeType.STAFFING;
                if (!isStaffingOffice(staffingOffice))
                    return;
                return staffingOffice;
            },
            serviceRegionNames: (shift) => {
                const serviceRegions = _r.shifts.getters.serviceRegions(shift);
                if (!serviceRegions)
                    return [];
                const serviceRegionNames = serviceRegions
                    .map((serviceRegion) => serviceRegion.name)
                    .filter(nullishFilter);
                return serviceRegionNames;
            },
            serviceRegions: (shift) => {
                var _a, _b;
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return;
                const itineraries = _r.shifts.getters.itineraries(shift);
                const directServiceRegions = (_b = (_a = _r.serviceRegionCache.getRaw(s.id)) === null || _a === void 0 ? void 0 : _a.serviceRegions) !== null && _b !== void 0 ? _b : {};
                const itineraryServiceRegions = Object.values(itineraries)
                    .reduce((acc, i) => {
                    const srs = _r.itineraries.getters.serviceRegionsFromCache(i);
                    if (!srs)
                        return acc;
                    return { ...acc, ...srs };
                }, {});
                const serviceRegions = {
                    ...directServiceRegions,
                    ...itineraryServiceRegions,
                };
                return Object.values(serviceRegions);
            },
            serviceRegionsCentroid: (shift) => {
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return;
                const shiftServiceRegions = _r.shifts.getters.serviceRegions(shift);
                if (!(shiftServiceRegions === null || shiftServiceRegions === void 0 ? void 0 : shiftServiceRegions.length))
                    return;
                const allCenters = shiftServiceRegions.map((sr) => {
                    const polygon = _r.serviceRegionPolygons.getRaw(sr.id);
                    if (!polygon)
                        return;
                    return getPolygonOrMultiPolygonCentroid(polygon.polygon);
                }).filter(nullishFilter);
                if (!allCenters.length)
                    return;
                return getAveragePoint(allCenters);
            },
            staffingOffices: (shift) => {
                var _a;
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return [];
                const staffingOffices = objectMap((_a = s.staffingOffices) !== null && _a !== void 0 ? _a : {}, (so) => _r.offices.getRaw(so.id));
                if (!staffingOffices)
                    return [];
                const isStaffingOffice = (o) => (o === null || o === void 0 ? void 0 : o.type) === OfficeType.STAFFING;
                return Object.values(staffingOffices).filter(isStaffingOffice);
            },
            startOfDayUTC: (shift) => {
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return;
                const startOfDay = dayjs(s.startTime.timestamp)
                    .tz(s.startTime.timeZone)
                    .startOf('day')
                    .valueOf();
                return changeTimeZone(startOfDay, 'UTC').timestamp;
            },
            state: (shift) => {
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return [];
                const { states } = _r.shifts.getters.stateAndPostalCodes(shift);
                if (!states)
                    return [];
                return states;
            },
            stateAndPostalCodes: (shift) => {
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return { postalCodes: [], states: [] };
                const itineraries = _r.shifts.getters.itineraries(shift);
                const states = new Set();
                const postalCodes = new Set();
                Object.values(itineraries).forEach((i) => {
                    const statesAndPostalCodes = _r.itineraries.getters.stateAndPostalCodes(i);
                    if (!statesAndPostalCodes)
                        return;
                    statesAndPostalCodes.states.forEach((stateCode) => {
                        if (stateCode)
                            states.add(stateCode);
                    });
                    statesAndPostalCodes.postalCodes.forEach((postalCode) => {
                        if (postalCode)
                            postalCodes.add(postalCode);
                    });
                });
                return {
                    postalCodes: Array.from(postalCodes),
                    states: Array.from(states),
                };
            },
            totalDuration: (shift) => {
                const s = extractTarget(shift, _r.shifts.getRaw);
                if (!s)
                    return 0;
                const itineraries = _r.shifts.getters.itineraries(shift);
                const totalDuration = Object.values(itineraries).reduce((total, next) => total + next.duration, 0);
                if (!totalDuration)
                    return 0;
                return totalDuration;
            },
        }),
        users: recordManager({
            context: DbRef.ITINERARIES,
            label: 'user',
            pathGetter: getUserPath,
        })({
            fullName: (user) => {
                const u = extractTarget(user, _r.users.getRaw);
                if (!u)
                    return;
                return formatFullName(u, false, false);
            },
            imgSrc: (user) => {
                const u = extractTarget(user, _r.users.getRaw);
                if (!u)
                    return;
                return u.info.picture;
            },
            locationForShift: (user) => {
                var _a, _b;
                const u = extractTarget(user, _r.users.getRaw);
                if (!u)
                    return;
                let userLocation = (_a = u.shiftSettings) === null || _a === void 0 ? void 0 : _a.defaultLeaveFrom;
                /** Explicitly exclude lat:0, lng:0 locations */
                if (!(userLocation === null || userLocation === void 0 ? void 0 : userLocation.lat) && !(userLocation === null || userLocation === void 0 ? void 0 : userLocation.lng))
                    userLocation = (_b = u.location) === null || _b === void 0 ? void 0 : _b.current;
                if (!(userLocation === null || userLocation === void 0 ? void 0 : userLocation.lat) && !(userLocation === null || userLocation === void 0 ? void 0 : userLocation.lng))
                    userLocation = u.info.address;
                if (!(userLocation === null || userLocation === void 0 ? void 0 : userLocation.lat) && !(userLocation === null || userLocation === void 0 ? void 0 : userLocation.lng))
                    return;
                return userLocation;
            },
            shiftDistance: (user) => {
                var _a;
                const userLocation = _r.users.getters.locationForShift(user);
                if (!userLocation)
                    return;
                const shift = (_a = shiftAssignmentState.value) === null || _a === void 0 ? void 0 : _a.shift;
                if (!shift)
                    return;
                const firstWaypoint = _r.shifts.getters.firstWaypoint(shift);
                if (firstWaypoint) {
                    const distance = getLatLngDeviationInMiles(userLocation, firstWaypoint.location);
                    if (distance > 100)
                        return '100+ mi away';
                    return `${distance.toFixed(1)} mi away`;
                }
                const serviceRegionCentroid = _r.shifts.getters.serviceRegionsCentroid(shift);
                if (!serviceRegionCentroid)
                    return;
                const distance = getLatLngDeviationInMiles(userLocation, serviceRegionCentroid);
                const nearest = [10, 25, 50, 100]
                    .find((d) => distance <= d);
                if (!nearest) {
                    return '100+ mi away';
                }
                return `< ${nearest} mi away`;
            },
        }),
        waypointActions: recordManager({
            allowUnset: true,
            context: DbRef.ITINERARIES,
            label: 'waypoint action',
            /** TODO Remove once WithWorkflows is deprecated and integrated on types */
            pathGetter: getWaypointActionPath,
        })({
            childWorkflows: (waypointAction) => {
                const wpa = extractTarget(waypointAction, _r.waypointActions.getRaw);
                if (!wpa)
                    return {};
                const { procedures } = wpa;
                if (!procedures)
                    return {};
                return objectFilter(objectMap(procedures, ({ id }) => {
                    var _a;
                    const p = _r.procedures.getRaw(id);
                    if (!p)
                        return;
                    const workflows = (_a = _r.procedures.getters.workflows(p)) === null || _a === void 0 ? void 0 : _a[wpa.id];
                    return workflows;
                }), nullishFilter);
            },
            /** Duration of a waypoint action rounded to 0.01 minutes */
            duration: (waypointAction) => {
                var _a;
                const wpa = extractTarget(waypointAction, _r.waypointActions.getRaw);
                return Math.round(((_a = wpa === null || wpa === void 0 ? void 0 : wpa.duration.actionDuration) !== null && _a !== void 0 ? _a : 0) * 100) / 100;
            },
            isKitShipmentSupplyStrategy: (waypointAction) => {
                var _a;
                const wpa = extractTarget(waypointAction, _r.waypointActions.getRaw);
                if (!wpa)
                    return false;
                const waypoint = _r.waypointActions.getters.waypoint(wpa.id);
                const draftWaypoints = Object.keys((_a = waypoint === null || waypoint === void 0 ? void 0 : waypoint.draftWaypoints) !== null && _a !== void 0 ? _a : {});
                return draftWaypoints.some((dw) => { var _a; return ((_a = _r.draftWaypoints.getRaw(dw)) === null || _a === void 0 ? void 0 : _a.supplyStrategy) === DraftWaypointSupplyStrategy.KIT_SHIPMENT; });
            },
            patientIDs: (waypointAction) => {
                var _a;
                const patientIDs = {};
                const wpa = extractTarget(waypointAction, _r.waypointActions.getRaw);
                if (!wpa)
                    return patientIDs;
                objectMap((_a = _r.waypointActions.getters.procedures(wpa.id)) !== null && _a !== void 0 ? _a : {}, (procedure) => {
                    const booking = store.getters['procedures/getProcedureBooking'](procedure.id);
                    if (!booking)
                        return;
                    patientIDs[booking.patientID] = true;
                });
                return patientIDs;
            },
            procedures: (waypointAction) => {
                var _a, _b;
                return objectFilter(objectMap((_b = (_a = extractTarget(waypointAction, _r.waypointActions.getRaw)) === null || _a === void 0 ? void 0 : _a.procedures) !== null && _b !== void 0 ? _b : {}, (_, id) => { var _a; return (_a = _r.procedures.getRaw(id)) !== null && _a !== void 0 ? _a : store.getters['procedures/getProcedureByID'](id); }), nullishFilter);
            },
            shipments: (waypointAction) => {
                var _a;
                const wpa = extractTarget(waypointAction, _r.waypointActions.getRaw);
                return objectFilter(objectMap((_a = wpa === null || wpa === void 0 ? void 0 : wpa.shipments) !== null && _a !== void 0 ? _a : {}, (_, id) => _r.draftShipments.getRaw(id)), nullishFilter);
            },
            /** If there is only one patient ID, return it, otherwise return undefined */
            singlePatientID: (waypointAction) => {
                const wpa = extractTarget(waypointAction, _r.waypointActions.getRaw);
                if (!wpa)
                    return;
                const patientIDs = _r.waypointActions.getters.patientIDs(wpa.id);
                return Object.keys(patientIDs).length === 1 ? Object.keys(patientIDs)[0] : undefined;
            },
            someProcedureIsMailInOnly: (waypointAction) => {
                const procedures = _r.waypointActions.getters.procedures(waypointAction);
                return Object.values(procedures).some((procedure) => _r.procedures.getters.isMailInOnly(procedure.id));
            },
            someProcedureRequiresOfflineSupplies: (waypointAction) => {
                const procedures = _r.waypointActions.getters.procedures(waypointAction);
                return Object.values(procedures).some((procedure) => {
                    var _a;
                    const dwp = _r.draftWaypoints.getRaw((_a = procedure.draftWaypointID) !== null && _a !== void 0 ? _a : '');
                    if (!dwp)
                        return false;
                    return dwp.supplyStrategy === DraftWaypointSupplyStrategy.OFFLINE_SUPPLIES;
                });
            },
            waypoint: (waypointAction) => {
                var _a;
                const wpa = extractTarget(waypointAction, _r.waypointActions.getRaw);
                if (!wpa)
                    return null;
                return _r.waypoints.getRaw((_a = wpa.waypointID) !== null && _a !== void 0 ? _a : '');
            },
            workflowContexts: (waypointAction) => {
                var _a;
                const wpa = extractTarget(waypointAction, _r.waypointActions.getRaw);
                if (!wpa)
                    return undefined;
                const { waypointID } = wpa;
                if (!waypointID)
                    return undefined;
                const wpWorkflow = (_a = _r.waypoints.getters.workflows(waypointID)[itinerary.id]) === null || _a === void 0 ? void 0 : _a.default;
                if (!wpWorkflow)
                    return undefined;
                const workflowContexts = new Set();
                wpWorkflow.forEach((wf) => {
                    var _a;
                    if (!((_a = wf.recordMeta.childWorkflow) === null || _a === void 0 ? void 0 : _a.context))
                        return;
                    workflowContexts.add(wf.recordMeta.childWorkflow.context);
                    if (!wf.recordMeta.childWorkflow.failureContext)
                        return;
                    workflowContexts.add(wf.recordMeta.childWorkflow.failureContext);
                });
                return workflowContexts;
            },
            workflowItemsFlattened: (waypointAction) => {
                const workflows = _r.waypointActions.getters.workflows(waypointAction);
                if (!workflows)
                    return [];
                return Object.values(workflows).flatMap((wf) => Object.values(wf)).flat();
            },
            workflows: (waypointAction) => {
                var _a;
                const workflowContexts = _r.waypointActions.getters.workflowContexts(waypointAction);
                const wpa = extractTarget(waypointAction, _r.waypointActions.getRaw);
                if (!workflowContexts || !wpa)
                    return undefined;
                const workflows = Object.fromEntries(Array.from(workflowContexts).map((context) => {
                    var _a, _b;
                    return [
                        context,
                        getWaypointActionWorkflow(waypointAction, context, (_a = wpa.waypointID) !== null && _a !== void 0 ? _a : '', _r, (_b = itinerary.forcePatientConsentWorkflowItem) !== null && _b !== void 0 ? _b : false).map((i) => {
                            var _a;
                            const item = cloneDeep(i);
                            item.recordMeta.parent = {
                                ...item.recordMeta.parent,
                                context,
                                referenceID: (_a = wpa.waypointID) !== null && _a !== void 0 ? _a : '',
                                storeRef: DbRef.WAYPOINTS,
                            };
                            return item;
                        }),
                    ];
                }));
                return {
                    [(_a = wpa.waypointID) !== null && _a !== void 0 ? _a : '']: workflows,
                };
            },
        }, {
            combinableMatch: (waypointAction0, waypointAction1) => {
                const wpa0 = extractTarget(waypointAction0, _r.waypointActions.getRaw);
                const wpa1 = extractTarget(waypointAction1, _r.waypointActions.getRaw);
                if (!wpa0 || !wpa1)
                    return false;
                /**
                 * Check if the waypoint actions are the same except for id and status
                 */
                const wpa0Copy = cloneDeep(wpa0);
                const wpa1Copy = cloneDeep(wpa1);
                delete wpa0Copy.id;
                delete wpa0Copy.status;
                delete wpa1Copy.id;
                delete wpa1Copy.status;
                const isDuplicate = isEqual(wpa0Copy, wpa1Copy);
                /**
                 * Check if the waypoint actions are the same patient
                 */
                const singlePatientID0 = _r.waypointActions.getters.singlePatientID(wpa0.id);
                const singlePatientID1 = _r.waypointActions.getters.singlePatientID(wpa1.id);
                const isSamePatient = singlePatientID0 === singlePatientID1;
                /**
                 * Check if the waypoint actions are the same type
                 */
                const isSameType = wpa0.type === wpa1.type;
                return isDuplicate || (isSamePatient && isSameType);
            },
            initWaypointActionSuppliesToScan: (waypointAction) => {
                var _a;
                const procedures = Object.values((_a = _r.waypointActions.getters.procedures(waypointAction)) !== null && _a !== void 0 ? _a : {});
                if (procedures.length === 0)
                    return [];
                const getScannableItem = (supplyItemWithSupplyID) => {
                    var _a;
                    const supply = store.getters['variables/getSupplyByID'](supplyItemWithSupplyID.supplyID);
                    if (!supply)
                        return;
                    const supplyItem = supplyItemWithSupplyID.id
                        ? (_a = store.state.supplies.supplyItems) === null || _a === void 0 ? void 0 : _a[supplyItemWithSupplyID.id]
                        : null;
                    return {
                        color: supply === null || supply === void 0 ? void 0 : supply.color,
                        imageColor: supply.icon,
                        isScanned: false,
                        key: generateID(),
                        name: supply === null || supply === void 0 ? void 0 : supply.name,
                        serial: supplyItem === null || supplyItem === void 0 ? void 0 : supplyItem.serial,
                        supplyID: supply === null || supply === void 0 ? void 0 : supply.id,
                    };
                };
                /**
                 * The clinician must unlock both kit supply items and procedure bags
                 *
                 * At this stage, a procedure will have either a kit supply item or a
                 * procedure bag
                 */
                const supplies = (procedures !== null && procedures !== void 0 ? procedures : []).map((procedure) => {
                    if (hasStatus(procedure, ProcedureStatus.CANCELED))
                        return;
                    if (procedure.kitSupplyItem)
                        return getScannableItem(procedure.kitSupplyItem);
                    return undefined;
                }).filter(nullishFilter);
                return supplies;
            },
            // TODO: DELETE, now using the scannableItemsByWorkflowItemID function and paradigm
            initialScannableKitSupply: (wa, _) => {
                var _a;
                return [(_a = extractTarget(wa, _r.waypointActions.getRaw)) === null || _a === void 0 ? void 0 : _a.id]
                    .filter(nullishFilter)
                    .flatMap((id) => _r.waypointActions.getters.initWaypointActionSuppliesToScan(id));
            },
            scannableItemsByWorkflowItemID: (wpa) => {
                const waypointAction = extractTarget(wpa, _r.waypointActions.getRaw);
                if (!waypointAction)
                    return {};
                const procedures = Object.values(_r.waypointActions.getters.procedures(waypointAction));
                return {
                    [WaypointActionWorkflowItemID.SCAN_MAIL_IN_BOX]: getMailInBoxSupply(waypointAction),
                    [WaypointActionWorkflowItemID.PACK_SPECIMEN_BAGS]: getPackingScanSpecimenBags(procedures),
                    [WaypointActionWorkflowItemID.UNLOCK_SUPPLIES]: {
                        items: _r.waypointActions.getters.initWaypointActionSuppliesToScan(waypointAction),
                        title: !store.getters['variables/getFeatureFlagByName'](FeatureFlag.DISABLE_PATIENT_VISIT_SUPPLY_SCANNING)
                            ? 'Scan the barcode on the following procedure kits'
                            : 'Click on the supplies below to unlock them.',
                    },
                };
            },
            workflowScanTitle: (_) => !store.getters['variables/getFeatureFlagByName'](FeatureFlag.DISABLE_PATIENT_VISIT_SUPPLY_SCANNING)
                ? 'Scan the barcode on the following procedure kits'
                : 'Click on the supplies below to unlock them.',
        }),
        waypoints: recordManager({
            allowUnset: true,
            context: DbRef.ITINERARIES,
            label: 'waypoint',
            /** TODO: Remove  */
            pathGetter: getWaypointPath,
        })({
            allWaypointActionsCanceled: (waypoint) => {
                const waypointActions = _r.waypoints.getters.waypointActions(waypoint);
                return waypointActions.every((wpa) => hasStatus(wpa, WaypointActionStatusName.CANCELED));
            },
            allWaypointActionsHandled: (waypoint) => {
                const waypointActions = _r.waypoints.getters.waypointActions(waypoint);
                if (!waypointActions.length)
                    return false;
                const allWaypointActionsHandled = waypointActions.every((wpa) => hasStatus(wpa, [
                    WaypointActionStatusName.COMPLETE,
                    WaypointActionStatusName.CANCELED,
                ]));
                return allWaypointActionsHandled;
            },
            childWorkflows: (waypoint) => {
                var _a, _b;
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                if (!wp)
                    return {};
                return (_b = (_a = wp.waypointActions) === null || _a === void 0 ? void 0 : _a.reduce((acc, { id }) => {
                    var _a;
                    const wpa = _r.waypointActions.getRaw(id);
                    if (!wpa)
                        return acc;
                    const workflows = _r.waypointActions.getters.workflows(wpa);
                    return {
                        ...acc,
                        [id]: (_a = workflows === null || workflows === void 0 ? void 0 : workflows[wp.id]) !== null && _a !== void 0 ? _a : [],
                    };
                }, {})) !== null && _b !== void 0 ? _b : {};
            },
            dateTimeRequest: (waypoint) => {
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                if (!wp)
                    return;
                const wpProcedures = _r.waypoints.getters.procedures(wp.id);
                /** if some value does not match, return undefined */
                const dateInfo = Object.values(wpProcedures)
                    .map((procedure) => procedure.dateInfo)
                    .filter(nullishFilter);
                if (dateInfo.length === 0)
                    return undefined;
                const dateTimeRequest = dateInfo[0];
                return dateInfo.every((info) => isEqual(info, dateTimeRequest))
                    ? dateTimeRequest
                    : undefined;
            },
            draftWaypointMismatched: (_waypoint) => {
                const itineraryID = itinerary.id;
                const waypoint = extractTarget(_waypoint, _r.waypoints.getRaw);
                if (!waypoint)
                    return false;
                const getDraftWaypointMismatch = (draftWaypoint) => !isNullish(draftWaypoint.itineraryID)
                    && draftWaypoint.itineraryID !== itineraryID;
                const getDraftWaypoints = (wp) => {
                    var _a;
                    return Object.keys((_a = wp.draftWaypoints) !== null && _a !== void 0 ? _a : {})
                        .map((id) => _r.draftWaypoints.getRaw(id))
                        .filter(nullishFilter);
                };
                return getDraftWaypoints(waypoint).some(getDraftWaypointMismatch);
            },
            draftWaypoints: (waypoint) => {
                var _a;
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                if (!wp)
                    return {};
                return objectFilter(objectMap((_a = wp.draftWaypoints) !== null && _a !== void 0 ? _a : {}, (_, id) => _r.draftWaypoints.getRaw(id)), nullishFilter);
            },
            estimatedTransitDuration: (waypoint) => {
                var _a, _b;
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                if (!((_a = wp === null || wp === void 0 ? void 0 : wp.transitInfo) === null || _a === void 0 ? void 0 : _a.estimatedTransitDuration))
                    return;
                return timeInMsToHoursAndMinutes((_b = wp.transitInfo) === null || _b === void 0 ? void 0 : _b.estimatedTransitDuration);
            },
            formattedAddress: (waypoint) => {
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                return (wp === null || wp === void 0 ? void 0 : wp.location)
                    ? formatAddressUS(wp.location, 'Apt', AddressFormatType.MULTI_LINE)
                    : undefined;
            },
            hasMailInAction: (waypoint) => {
                const waypointActions = _r.waypoints.getters.waypointActions(waypoint);
                const hasMailInAction = waypointActions.some((wpa) => wpa.type === WaypointActionType.MAIL_IN);
                return hasMailInAction;
            },
            indexInItinerary: (waypoint) => {
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                if (!wp)
                    return -1;
                return _r.itinerary.getters.waypointIndexByID(wp.id);
            },
            isActionable: (waypoint) => {
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                if (!wp)
                    return false;
                const nextWaypoint = _r.itinerary.getters.nextActionableWaypoint();
                if (!nextWaypoint)
                    return false;
                const shift = _r.itinerary.getters.shift();
                if (!hasStatus(shift, [
                    ShiftStatus.IN_PROGRESS,
                    ShiftStatus.FAILED,
                ]))
                    return false;
                return wp.id === nextWaypoint.id;
            },
            isCurrentDate: (waypoint) => {
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                if (!wp || !wp.date)
                    return false;
                const scheduledDate = dayjs(wp.date.timestamp).format('MM/DD/YYYY');
                const currentDate = dayjs().format('MM/DD/YYYY');
                return scheduledDate === currentDate;
            },
            nurseDepartedLate: (waypoint) => {
                var _a, _b, _c;
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                if (!((_b = (_a = wp === null || wp === void 0 ? void 0 : wp.transitInfo) === null || _a === void 0 ? void 0 : _a.completionTime) === null || _b === void 0 ? void 0 : _b.timestamp))
                    return false;
                const waypointDuration = _r.waypoints.getters.totalDuration(wp.id);
                const expectedDepatureTime = dayjs((_c = wp.date) === null || _c === void 0 ? void 0 : _c.timestamp).add(waypointDuration, 'm');
                const actualDepartureTime = dayjs(wp.transitInfo.completionTime.timestamp);
                return actualDepartureTime.isAfter(expectedDepatureTime);
            },
            /** NOTE: This relationship is technically incorrect; multiple orders can be associated with a single waypoint */
            order: (waypoint) => {
                const orders = _r.waypoints.getters.orders(waypoint);
                if (Object.values(orders).length === 0)
                    return;
                return Object.values(orders)[0];
            },
            orders: (waypoint) => {
                const procedures = _r.waypoints.getters.procedures(waypoint);
                return objectFilter(objectMap(procedures, (procedure) => { var _a; return procedure.orderID ? (_a = store.state.orders.orders) === null || _a === void 0 ? void 0 : _a[procedure.orderID] : undefined; }, (_, { orderID }) => orderID), nullishFilter);
            },
            /** TODO: Determine if needed at this phase? */
            patientList: (waypoint) => {
                var _a, _b;
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                return (_b = (_a = wp === null || wp === void 0 ? void 0 : wp.waypointActions) === null || _a === void 0 ? void 0 : _a.map((waypointAction) => ({
                    patientID: _r.waypointActions.getters.singlePatientID(waypointAction.id),
                    waypointActionID: waypointAction.id,
                })).filter((p) => !isNullish(p.patientID) && !isNullish(p.waypointActionID))) !== null && _b !== void 0 ? _b : [];
            },
            procedures: (waypoint) => {
                var _a, _b;
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                return (_b = (_a = wp === null || wp === void 0 ? void 0 : wp.waypointActions) === null || _a === void 0 ? void 0 : _a.map(({ id }) => _r.waypointActions.getters.procedures(id)).reduce((acc, procedureIDs) => ({
                    ...acc,
                    ...procedureIDs,
                }), {})) !== null && _b !== void 0 ? _b : {};
            },
            scheduledDateTime: (waypoint) => {
                var _a;
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                if (!((_a = wp === null || wp === void 0 ? void 0 : wp.date) === null || _a === void 0 ? void 0 : _a.timestamp))
                    return;
                const timeZone = wp.date.timeZone || 'UTC';
                return dayjs(wp.date.timestamp).tz(timeZone).format('MMMM D, h:mm A z');
            },
            scheduledDay: (waypoint) => {
                var _a;
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                if (!wp)
                    return;
                return dayjs((_a = wp.date) === null || _a === void 0 ? void 0 : _a.timestamp).format('dddd');
            },
            scheduledTimeText: (waypoint) => {
                var _a;
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                if (!((_a = wp === null || wp === void 0 ? void 0 : wp.date) === null || _a === void 0 ? void 0 : _a.timestamp))
                    return;
                const scheduledForCurrentDay = dayjs(wp.date.timestamp).isSame(dayjs(), 'day');
                const formatString = scheduledForCurrentDay ? 'h:mm a' : 'ddd, MMM D h:mm a';
                const scheduledTime = dayjs(wp.date.timestamp).format(formatString);
                return `Scheduled for ${scheduledTime}`;
            },
            /**
             * Get service regions from the cache for a waypoint
             * If the service regions are not in the cache
             */
            serviceRegionsFromCache: (waypoint) => {
                var _a, _b;
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                if (!wp)
                    return {};
                const latLng = { lat: wp.location.lat, lng: wp.location.lng };
                const hash = generateHash(latLng);
                return (_b = (_a = _r.serviceRegionCache.getRaw(hash)) === null || _a === void 0 ? void 0 : _a.serviceRegions) !== null && _b !== void 0 ? _b : {};
            },
            stateAndPostalCode: (waypoint) => {
                var _a, _b;
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                if (!(wp === null || wp === void 0 ? void 0 : wp.location))
                    return;
                return {
                    postalCode: (_a = getPostalCodeByLocation(wp.location)) !== null && _a !== void 0 ? _a : '',
                    state: (_b = getStateByLocation(wp.location)) !== null && _b !== void 0 ? _b : '',
                };
            },
            timeZone: (waypoint) => {
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                return wp === null || wp === void 0 ? void 0 : wp.location.timeZone;
            },
            /** Total duration of a waypoint's actions */
            totalDuration: (waypoint) => {
                var _a;
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                if (!wp)
                    return 0;
                return ((_a = _r.waypoints.getters.waypointActions(wp.id)) !== null && _a !== void 0 ? _a : [])
                    .reduce((acc, curr) => {
                    var _a, _b;
                    const duration = (_b = (_a = curr === null || curr === void 0 ? void 0 : curr.duration) === null || _a === void 0 ? void 0 : _a.actionDuration) !== null && _b !== void 0 ? _b : 0;
                    return acc + duration;
                }, 0);
            },
            waypointActions: (waypoint) => {
                var _a, _b;
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                const wpas = (_b = (_a = wp === null || wp === void 0 ? void 0 : wp.waypointActions) === null || _a === void 0 ? void 0 : _a.map((waypointAction) => _r.waypointActions.getRaw(waypointAction.id)).filter(nullishFilter)) !== null && _b !== void 0 ? _b : [];
                return wpas;
            },
            /**
             * Used to determine the class extension for the waypoint letter
             */
            waypointLetterClassExtension: (wp) => {
                const waypoint = extractTarget(wp, _r.waypoints.getRaw);
                if (!waypoint)
                    return;
                switch (waypoint.type) {
                    case WaypointType.PICK_DROP: return '--pick-drop';
                    case WaypointType.PATIENT: return '--patient';
                    default: return undefined;
                }
            },
            workflows: (waypoint) => ({
                [itinerary.id]: {
                    default: getWaypointWorkflow(waypoint, 'default', itinerary.id, _r),
                },
            }),
        }, {
            /**
             * Determines if two waypoints match to a certain degree
             * i.e. same location, same type, and same place
             */
            combinableMatch: (_wp0, _wp1) => {
                const wp0 = extractTarget(_wp0, _r.waypoints.getRaw);
                const wp1 = extractTarget(_wp1, _r.waypoints.getRaw);
                if (!wp0 || !wp1)
                    return false;
                return wp0.location.lat === wp1.location.lat
                    && wp0.location.lng === wp1.location.lng
                    && wp0.type === wp1.type
                    && (!wp1.placeID
                        || wp0.placeID === wp1.placeID);
            },
            /**
             * Used to calculate deviation from a waypoint to a given location
             * Functionality shared by clinician deviation notification and dispatch
             * deviation failure display on ItineraryWaypointCard
             */
            deviation: (waypoint, loc, unit = 'feet') => {
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                if (!wp || !loc)
                    return 0;
                const { location } = wp;
                const deviation = unit === 'miles'
                    ? getLatLngDeviationInMiles(location, loc)
                    : getLatLngDeviationInFt(location, loc);
                return deviation;
            },
            /**
             * Determines if a waypoint is too far from the clinician's current location
             */
            deviationFailure: (waypoint) => {
                var _a, _b;
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                if (!wp)
                    return false;
                const loc = (_b = (_a = _r.itinerary.getters.assignedNurse()) === null || _a === void 0 ? void 0 : _a.location) === null || _b === void 0 ? void 0 : _b.current;
                if (!loc)
                    return false;
                const DEFAULT_MAX_DEVIATION = 1000;
                return _r.waypoints.getters.deviation(wp, loc) > DEFAULT_MAX_DEVIATION;
            },
            durationFailure: (waypoint) => {
                var _a, _b;
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                if (!wp)
                    return false;
                const startTime = (_b = (_a = _r.waypoints.getRaw(wp.id)) === null || _a === void 0 ? void 0 : _a.date) === null || _b === void 0 ? void 0 : _b.timestamp;
                if (isNullish(startTime))
                    return false;
                const diff = dayjs(Date.now()).diff(startTime, 'minute');
                const estimatedWaypointDuration = _r.waypoints.getters.totalDuration(wp.id);
                return diff > estimatedWaypointDuration;
            },
            /**
             * TODO: create shared logic between this and the logic used for the drop
             * off flow access
             *
             * migrated over from the old components/itinerary/ItineraryWaypointCard/helpers.ts
             * that originally came from the old itinerary flow on pro
             */
            waypointIsLocked: (waypoint) => {
                const wp = extractTarget(waypoint, _r.waypoints.getRaw);
                if (!wp)
                    return false;
                const { waypoints } = _r.itinerary.value;
                if (!waypoints)
                    return false;
                const indexOfCurrentWaypoint = waypoints.findIndex((idObj) => idObj.id === wp.id);
                if (indexOfCurrentWaypoint === 0)
                    return false;
                const idObjOfWaypointBefore = waypoints[indexOfCurrentWaypoint - 1];
                if (!idObjOfWaypointBefore)
                    return false;
                const previousWaypoint = _r.waypoints.getRaw(idObjOfWaypointBefore.id);
                return !hasStatus(previousWaypoint, WaypointStatusName.COMPLETE);
            },
        }),
    };
    const init = (initArgs) => {
        const { colorScheme = 'light', id, queueUpdate = false, userID, } = initArgs !== null && initArgs !== void 0 ? initArgs : {};
        const initial = {
            ...initItinerary(),
            ...id && _r.itineraries.getRaw(id),
        };
        state.set.isReady(false);
        const newItinerary = injectNulls(initial);
        if (id)
            newItinerary.id = id;
        if (userID)
            newItinerary.createdBy = userID;
        _r.itinerary.set.value(newItinerary);
        state.set.colorScheme(colorScheme);
        if (queueUpdate) {
            useDbSyncStore().queueNextUpdate(getItineraryPath(newItinerary.id), 'initialize new itinerary', newItinerary, DbRef.ITINERARIES);
        }
    };
    watch(() => itinerary.id, (id) => {
        useDbSyncStore().provideContext(DbRef.ITINERARIES, id);
    }, { immediate: true });
    // Watch for waypoints to determine if there are any itinerary updates that need to happen
    watch(_r.itinerary.getters.currentWaypoints, (waypoints) => {
        if (!waypoints.length)
            return;
        // If the any waypoints are in-transit, set the itinerary to in-progress
        const shouldItineraryBeStarted = waypoints.some((wp) => hasStatus(wp, WaypointStatusName.TRANSIT_WAYPOINT));
        const itineraryIsNotInProgress = !hasStatus(_r.itinerary.value, ItineraryStatus.IN_PROGRESS);
        if (shouldItineraryBeStarted && itineraryIsNotInProgress) {
            _r.itinerary.set.status(ItineraryStatus.IN_PROGRESS, `Setting ${_r.itinerary.value.id} itinerary status to in progress`);
        }
        // If all waypoints are complete, set the itinerary to completed
        const areAllWaypointsComplete = waypoints.every((wp) => hasStatus(wp, WaypointStatusName.COMPLETE));
        if (areAllWaypointsComplete) {
            _r.itinerary.set.status(ItineraryStatus.COMPLETED, `Setting ${_r.itinerary.value.id} itinerary status to completed`);
        }
    }, { deep: true, immediate: true });
    /**
     * NOTE: This is a temporary measure to keep the user object available in the Portal module
     * until we migrate the entire app to Pinia Store.
     */
    watch(() => store.state.auth.user, (user, oldUser) => {
        if (!user)
            return;
        _r.users.set(cloneDeep(user));
        if (user.role === (oldUser === null || oldUser === void 0 ? void 0 : oldUser.role))
            return;
        viewAs.value = user.role;
    }, { deep: true, immediate: true });
    return {
        ..._r,
        init,
        setViewAs,
        shiftAssignmentState,
        state,
        viewAs: viewAsRoleGetters,
    };
});
const isRecordManagerWithScannableItems = (manager) => 'scannableItemsByWorkflowItemID' in manager.getters;

export { isRecordManagerWithScannableItems, usePortalStore };
